<template>
  <main
    class="home-page"
    :class="{
      'is-entrance-ready': homeEntranceReady,
      'is-card-drawing': homeCardsDrawing,
      'is-mobile-reveal-ready': mobileRevealReady,
    }"
  >
    <div class="home-core">
    <div class="home-hero-section">
    <section class="home-hero-media" aria-label="Scenes from Henry's travels">
      <img
        class="home-video-poster"
        src="/assets/img/home5-poster.jpg"
        alt=""
        decoding="async"
        fetchpriority="high"
        aria-hidden="true"
      >
      <video
        class="homevideo5"
        id="videoElement"
        muted
        autoplay
        playsinline
        webkit-playsinline
        preload="metadata"
        :role="mobileHomeViewport ? null : 'button'"
        :tabindex="mobileHomeViewport ? -1 : 0"
        :aria-label="mobileHomeViewport ? null : 'Play video sound'"
        :aria-hidden="mobileHomeViewport ? 'true' : null"
      >
        <source src="/assets/vid/home5.mp4" type="video/mp4">
      </video>
      <img id="imageElement" src="/assets/img/home1.jpg" alt="" class="fallback-image" style="display: none;">
      <div class="transition-overlay" id="transitionOverlay" aria-hidden="true">
        <span>Shot in Warrnambool&nbsp;&nbsp;06/2025</span>
      </div>
      <div class="description-overlay" id="descriptionOverlay">
        <div class="description-content" id="descriptionContent"></div>
      </div>
    </section>

    <section class="home-hero-panel" aria-labelledby="welcomeTitle">
      <div class="home-hero-copy welcome-text">
        <h1 id="welcomeTitle">
          <span>Henry</span>
          <span>Wang<span id="compassContainer"><i class="far fa-compass" id="compassIcon"></i></span></span>
        </h1>
        <h2
          id="welcomeSubtitle"
          v-text="'I explore.\nI try to understand.\nI build from there.'"
        ></h2>
      </div>

      <div class="home-hero-mobile-scene">
        <router-link
          class="home-hero-mobile-note"
          :to="{ path: '/education', hash: '#study-hong-kong' }"
          aria-label="Read about Applied Artificial Intelligence at HKU"
        >
          <span>Applied AI</span>
          <strong>HKU</strong>
        </router-link>
        <router-link
          class="home-hero-mobile-pwc"
          :to="{ path: '/experience', hash: '#work-pwc' }"
          aria-label="Read about Henry's role at PwC"
        >
          <span class="home-hero-mobile-pwc-label">Cloud, Data &amp; AI</span>
          <strong class="home-hero-mobile-pwc-title">PwC</strong>
        </router-link>
      </div>

      <nav class="home-hero-socials" aria-label="Henry's social profiles">
        <a href="https://www.instagram.com/henry_w_hy/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
          <i class="fab fa-instagram" aria-hidden="true"></i>
        </a>
        <a href="https://www.linkedin.com/in/henrywhy/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
          <i class="fab fa-linkedin" aria-hidden="true"></i>
        </a>
        <a href="https://github.com/henryhyw/" target="_blank" rel="noopener noreferrer" aria-label="GitHub">
          <i class="fab fa-github" aria-hidden="true"></i>
        </a>
      </nav>
    </section>
    </div>

    <section
      ref="homeAskPanel"
      class="home-ask-panel"
      :class="{
        'is-engaged': homeAskEngaged,
        'is-mobile-input-docked': mobileAskInputDocked,
      }"
      aria-labelledby="homeAskTitle"
    >
      <div class="home-section-eyebrow"><span>Ask the site</span><i aria-hidden="true"></i></div>
      <nav class="home-ask-focus" aria-label="Current focus">
        <router-link
          v-for="item in focusItems"
          :key="item.id"
          :to="item.anchor ? { path: item.route, hash: `#${item.anchor}` } : { path: item.route }"
        >
          <span class="home-ask-focus-copy"><strong>{{ item.title }}</strong><small>{{ item.subtitle }}</small></span>
          <ArrowUpRightIcon class="home-ask-focus-arrow" />
        </router-link>
      </nav>
      <button
        type="button"
        class="home-ask-focus-return"
        aria-label="Back"
        @click="resetHomeAsk"
      >
        <i class="fa fa-arrow-left" aria-hidden="true"></i>
        <span>Back</span>
      </button>
      <div class="home-ask-stage">
        <header class="home-ask-intro">
          <h2 id="homeAskTitle">
            <span class="home-ask-title-wide">What<br>would you<br>like to know?</span>
            <span class="home-ask-title-mobile">What would<br>you like to<br>know?</span>
          </h2>
          <p>Ask about my work, research, or ideas.</p>
        </header>

        <div id="homeAsk" class="home-ask">
        <div class="home-ask-bar-slot">
          <div class="home-ask-bar" @click="focusAskInput">
            <svg class="home-ask-spark" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3 L13.5 9.5 L20 11 L13.5 12.5 L12 19 L10.5 12.5 L4 11 L10.5 9.5 Z"/></svg>
            <div class="home-ask-input-wrap">
              <input
                ref="askInput"
                v-model="askText"
                type="text"
                class="home-ask-input"
                placeholder=""
                aria-label="Ask this site anything about Henry"
                @focus="askFocused = true"
                @blur="askFocused = false"
                @compositionstart="askComposing = true"
                @compositionend="askComposing = false"
                @keydown.enter="submitAsk($event)"
              >
              <transition v-if="showExamplePrompt" name="home-prompt-fade" mode="out-in">
                <span :key="askPromptText" class="home-ask-prompt" aria-hidden="true">{{ askPromptText }}</span>
              </transition>
              <span v-else-if="inputEmpty" class="home-ask-prompt" aria-hidden="true">{{ askPromptText }}</span>
            </div>
            <button
              type="button"
              class="home-ask-submit"
              aria-label="Submit question"
              @click.stop="submitAsk()"
            >→</button>
          </div>
        </div>

        <div
          v-if="askAnswerText || askBusy || askError || askMapVisible || viewBusy || viewError"
          class="home-ask-result"
          :class="{
            'home-ask-result-busy': !askError && !viewError
              && (askBusy || viewBusy || askMapVisible)
              && (!askAnswerText || viewBusy),
          }"
        >
          <ViewComposeProgress v-if="viewBusy" :started-at="viewStartedAt" />
          <template v-else-if="askAnswerText">
            <transition appear @before-enter="beforeHomeAskEnter" @enter="enterHomeAsk" @leave="leaveHomeAsk">
              <p class="home-ask-answer" v-html="askAnswerHtml"></p>
            </transition>
            <AskAnswerAudio v-if="!askBusy" :text="askAnswerText" />
            <AskActions v-if="askActionsList.length && askAnswerText && !askBusy && !viewBusy" :actions="askActionsList" surface="home" />
          </template>

          <TextShimmerWave
            v-else-if="askBusy"
            :children="askStatus || 'Thinking…'"
            as="div"
            class-name="home-ask-status home-ask-thinking"
          />
          <p v-else-if="viewError" class="home-ask-status" role="alert">{{ viewError }}</p>
          <p v-else-if="askError" class="home-ask-status">{{ askErrorText }}</p>

          <div v-if="viewOfferVisible && askAnswerText && !askBusy && !askError && !viewBusy" class="home-view-offer">
            <div class="home-view-offer-copy">
              <strong>{{ returnViewAvailable ? 'Your composed View is ready' : viewOffer.title }}</strong>
              <span v-if="returnViewAvailable">Continue through the sources and connections shaped around your question.</span>
              <span v-else>{{ viewOffer.description }}</span>
            </div>
            <button
              type="button"
              class="home-view-offer-button"
              @click="createViewFromAnswer"
            >
              {{ returnViewAvailable ? 'Return to the View' : 'Explore the story' }} <span class="home-view-offer-arrow" aria-hidden="true">→</span>
            </button>
          </div>

          <div v-if="homeCitedSources.length && !askBusy && !viewBusy" class="home-ask-sources-wrap">
            <span class="home-ask-sources-label" aria-hidden="true">Sources</span>
            <transition-group
              appear
              tag="div"
              class="home-ask-sources"
              @before-enter="beforeHomeChipEnter"
              @enter="enterHomeChip"
              @leave="leaveHomeChip"
            >
              <button
                v-for="s in homeCitedSources"
                :key="s.id"
                :data-source-key="s.id"
                class="home-ask-source"
                :class="{ 'is-previewed': hoveredSourceId === s.id }"
                @mouseenter="hoveredSourceId = s.id"
                @mouseleave="hoveredSourceId = null"
                @focus="hoveredSourceId = s.id"
                @blur="hoveredSourceId = null"
                @click="goSource(s)"
              >{{ s.page }} › {{ s.title }}</button>
            </transition-group>
          </div>

          <div v-if="followUpQuestions.length && askAnswerText && !askBusy && !viewBusy" class="home-ask-followups">
            <span class="home-ask-followups-label" aria-hidden="true">Keep exploring</span>
            <div class="home-ask-followups-list">
              <button
                v-for="q in followUpQuestions"
                :key="q"
                type="button"
                class="home-ask-followup"
                @click="askFollowUp(q)"
              >{{ q }}</button>
            </div>
          </div>

          <div v-if="(askMapVisible || askBusy) && !viewBusy" class="home-ask-map-shell">
            <EmbeddingMap
              ref="homeAskMap"
              class="home-ask-map"
              compact
              fit-stage
              initial-view-mode="3d"
              :monochrome="true"
              :show-controls="false"
              :show-view-toggle="false"
              :show-caption="false"
              :show-hits="false"
              :allow-navigation="homeAskMapNavigable"
              two-finger-touch
              wheel-zoom-requires-pinch
              @ready="syncHomeAskMap"
            />
          </div>
        </div>
        </div>
      </div>
    </section>
    </div>

    <section class="home-current-panel" aria-labelledby="homeCurrentTitle">
      <div class="home-section-eyebrow"><span>Currently</span><i aria-hidden="true"></i></div>
      <h2 id="homeCurrentTitle">Current focus</h2>
      <nav class="home-current-list" aria-label="Current work and ideas">
        <router-link
          v-for="(item, index) in focusItems"
          :key="item.id"
          :to="item.anchor ? { path: item.route, hash: `#${item.anchor}` } : { path: item.route }"
        >
          <span class="home-current-number">{{ String(index + 1).padStart(2, '0') }}</span>
          <span class="home-current-copy"><strong>{{ item.title }}</strong><small>{{ item.subtitle }}</small></span>
          <ArrowUpRightIcon class="home-current-arrow" />
        </router-link>
      </nav>
      <router-link class="home-current-cv" to="/cv">Open CV <span aria-hidden="true">↗</span></router-link>
    </section>

    <section class="home-site-index" aria-labelledby="homeSiteIndexTitle">
      <div class="home-section-eyebrow"><span id="homeSiteIndexTitle">Index</span><i aria-hidden="true"></i></div>
      <nav aria-label="Site index">
        <router-link v-for="(item, index) in siteIndexItems" :key="item.route" :to="item.route">
          <span>{{ item.label }}</span>
          <small>{{ String(index + 1).padStart(2, '0') }}</small>
        </router-link>
      </nav>
    </section>

    <div id="overlay"></div>
    <teleport to="body">
      <transition name="home-preview-fade">
        <div v-if="sourcePreview" :key="sourcePreview.id" class="home-ask-preview" :style="previewStyle" aria-hidden="true">
          <span class="home-ask-preview-kicker">{{ sourcePreview.page }} › {{ sourcePreview.title }}</span>
          <p class="home-ask-preview-text">{{ sourcePreview.snippet }}</p>
        </div>
      </transition>
    </teleport>
  </main>
</template>

<script>
    import EmbeddingMap from './EmbeddingMap.vue';
    import ViewComposeProgress from './view/ViewComposeProgress.vue';
    import TextShimmerWave from './ui/TextShimmerWave.vue';
    import AskAnswerAudio from './ui/AskAnswerAudio.vue';
    import AskActions from './ui/AskActions.vue';
    import ArrowUpRightIcon from './ui/ArrowUpRightIcon.vue';
    import { animate } from 'motion';
    import { askConfigured, askStream, askErrorMessage } from '../utils/askSite';
    import { ASK_SAMPLE_PROMPTS, HOME_ASK_INTRO_PROMPT } from '../utils/askPrompts';
    import { currentlyItems } from '../data/currently';
    import { semanticTopK } from '../utils/semanticSearch';
    import {
        allAskContexts,
        diversifyAskWritingContexts,
        askAnswerFollowUps,
        askResponseIsComplete,
        citedSourcesFromAnswer,
        cleanAskAnswer,
        cleanAskContextText,
        groundAskFollowUps,
        askViewRouting,
        askViewOffer,
        looksLikeViewBuildRequest,
        loadSearchItems,
        sourceDisplayTitle,
        takeRetrievalTrace,
    } from '../utils/askRetrieval';
    import { askActionsForDisplay, renderAskAnswerLinks } from '../utils/askActions';
    import { logAskClient, logEvent, newAskContext, newId, sessionId } from '../utils/askTelemetry';
    import {
        loadAskSession,
        addAskTurn,
        setAskView,
        setAskOrigin,
        recentAskTurns,
        askSourceIdsInRecent,
        recentDuplicateViewQuestion,
    } from '../utils/askSession';
    import {
        beginViewCompose,
        cleanViewIntent,
        developViewDelay,
        DEVELOP_VIEW_LABEL,
        DEVELOP_VIEW_ROUTE,
        endViewCompose,
        isDevelopMode,
        isViewComposing,
        requestView,
    } from '../utils/viewSite';
    import { candidatesForView } from '../utils/viewRetrieval';
    import { sanitizeViewResponse, validateViewResponse } from '../utils/viewValidation';
    import { settleScrollToId } from '../utils/anchorLanding';
    import { sourceRouteTarget } from '../utils/viewSourceTarget';
    import { schedulePublicViewsPreload } from '../utils/viewShareSite';

    const HOME_ASK_STORAGE_KEY = 'homeAskState:v1';
    let homeAskMemory = null;

    function isPhysicalPhoneBrowser() {
        if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
        const coarsePointer = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
        const phoneWidth = Math.min(window.innerWidth, window.screen?.width || window.innerWidth) <= 760;
        return phoneWidth && navigator.maxTouchPoints > 0 && (coarsePointer || 'ontouchstart' in window);
    }

    function shouldSettleRestoredMobileHome() {
        // Diagnostic isolation. A physical phone receives the complete visual
        // state on its first Vue render and runs no Home-owned entrance or
        // section-reveal animation. This separates application motion from a
        // browser compositor, font, or page-snapshot repaint.
        return isPhysicalPhoneBrowser();
    }

    function compactAskSources(sources) {
        return (sources || []).map((s) => ({
            id: s.id,
            route: s.route,
            anchor: s.anchor || '',
            page: s.page,
            title: s.title,
        }));
    }

    function readHomeAskState() {
        if (homeAskMemory) return homeAskMemory;
        if (typeof window === 'undefined' || !window.sessionStorage) return null;
        try {
            const raw = window.sessionStorage.getItem(HOME_ASK_STORAGE_KEY);
            homeAskMemory = raw ? JSON.parse(raw) : null;
            return homeAskMemory;
        } catch (err) {
            return null;
        }
    }

    function writeHomeAskState(state) {
        homeAskMemory = state;
        if (typeof window === 'undefined' || !window.sessionStorage) return;
        try {
            window.sessionStorage.setItem(HOME_ASK_STORAGE_KEY, JSON.stringify(state));
        } catch (err) {
            // Storage can be unavailable in private modes; in-memory state still covers route switches.
        }
    }

    function collectViewSourceIds(view) {
        const ids = [];
        for (const section of (view && view.sections) || []) {
            for (const block of (section && section.blocks) || []) {
                if (block && block.source_id) ids.push(block.source_id);
                if (block && block.source_ids) ids.push(...block.source_ids);
            }
        }
        return [...new Set(ids)];
    }

    export default {
        name: 'IndexPage',
        components: { ArrowUpRightIcon, AskActions, AskAnswerAudio, EmbeddingMap, TextShimmerWave, ViewComposeProgress },
        data() {
            const settleRestoredMobileHome = shouldSettleRestoredMobileHome();
            return {
                scriptModule: null,
                askText: '',
                askFocused: false,
                askComposing: false,
                askAnswer: '',
                askSources: [],
                askBusy: false,
                askError: '',
                askStatus: '',
                askMapVisible: false,
                askMapHits: [],
                askMapLabel: '',
                askMapFinalized: false,
                askIntroPrompt: HOME_ASK_INTRO_PROMPT,
                askQuestions: ASK_SAMPLE_PROMPTS,
                askQIndex: -1,
                askRotate: null,
                homeScene: null,
                currentItems: currentlyItems,
                focusItems: [
                    currentlyItems.find((item) => item.id === 'slidepoise'),
                    currentlyItems.find((item) => item.id === 'searchstory'),
                    currentlyItems.find((item) => item.id === 'structure-settles-thought'),
                    currentlyItems.find((item) => item.id === 'location'),
                ].filter(Boolean),
                siteIndexItems: [
                    { label: 'About', route: '/about' },
                    { label: 'Study', route: '/education' },
                    { label: 'Work', route: '/experience' },
                    { label: 'Research', route: '/research' },
                    { label: 'Builds', route: '/projects' },
                    { label: 'Fragments', route: '/fragments' },
                    { label: 'Views', route: '/views' },
                ],
                homeIntroStage: 0,
                settleRestoredMobileHome,
                homeEntranceReady: settleRestoredMobileHome,
                homeCardsDrawing: false,
                mobileRevealReady: settleRestoredMobileHome,
                mobileHomeViewport: typeof window !== 'undefined' && window.innerWidth <= 1100,
                hoveredSourceId: null,
                previewStyle: null,
                // null = AI follow-ups not grounded yet; [] = grounded, none passed.
                groundedFollowUps: null,
                viewOfferVisible: false,
                returnViewAvailable: false,
                viewBusy: false,
                viewStartedAt: 0,
                viewError: '',
                mobileAskInputDocked: false,
            };
        },
        computed: {
            // Idle: cycle through real example questions in a separate overlay so
            // each change can fade instead of snapping like a native placeholder.
            askPromptText() {
                if (this.askFocused) return 'Ask anything about Henry…';
                if (this.askQIndex < 0) return this.askIntroPrompt;
                return this.askQuestions[this.askQIndex];
            },
            // Treat an in-progress IME composition (pinyin not yet committed) as "not empty",
            // so the placeholder hides the moment the user starts typing — otherwise it stays
            // up and overlaps the characters being composed in the input.
            inputEmpty() {
                return !this.askText && !this.askComposing;
            },
            showExamplePrompt() {
                return !this.askFocused && this.inputEmpty;
            },
            askAnswerText() {
                return cleanAskAnswer(this.askAnswer);
            },
            askAnswerHtml() {
                return renderAskAnswerLinks(this.askAnswerText);
            },
            askActionsList() {
                return askActionsForDisplay(this.askAnswer, this.viewOfferVisible ? 'offer' : 'none');
            },
            viewOffer() {
                return askViewOffer(this.askAnswer);
            },
            homeCitedSources() {
                return citedSourcesFromAnswer(this.askAnswer, this.askSources);
            },
            askErrorText() {
                return askErrorMessage();
            },
            homeAskMapNavigable() {
                // Clickable once the request settles, whether it produced an answer
                // or errored out: the map still holds the retrieved passages, so the
                // visitor can jump into them even when generation failed.
                return !this.askBusy && (!!this.askAnswerText || !!this.askError);
            },
            homeAskEngaged() {
                return !!(
                    this.askBusy
                    || this.askAnswerText
                    || this.askError
                    || this.askMapVisible
                    || this.viewBusy
                    || this.viewError
                );
            },
            // Hovering a cited source peeks at its passage without leaving the page.
            sourcePreview() {
                if (!this.hoveredSourceId) return null;
                const s = this.homeCitedSources.find((x) => x.id === this.hoveredSourceId);
                if (!s) return null;
                const text = cleanAskContextText(s.body || '').replace(/\s+/g, ' ').trim();
                const snippet = text
                    ? (text.length > 230 ? `${text.slice(0, 230).trim()}…` : text)
                    : 'Open the passage to read it.';
                return { id: s.id, page: s.page, title: s.title, snippet };
            },
            // A small set of next questions: deepen into the cited passages, then
            // fall back to the general pool. One answer becomes a short exploration.
            followUpQuestions() {
                if (this.askBusy || !this.askAnswerText) return [];
                // Only show the model's questions for this answer after they
                // have been grounded against the site's own retrieval index.
                const rawAiFollowUps = askAnswerFollowUps(this.askAnswer);
                if (this.groundedFollowUps !== null) {
                    return this.groundedFollowUps;
                }
                return rawAiFollowUps;
            },
        },
        watch: {
            hoveredSourceId(id) {
                this.positionSourcePreview(id);
            },
            askText: 'persistHomeAskState',
            askAnswer() {
                this.persistHomeAskState();
                // Answer replacement can trigger browser scroll anchoring.
                // Treat the resulting layout pass separately from a visitor's
                // next scroll so a short reply cannot undock the composer.
                this._mobileAskDockLastScrollY = null;
                this.$nextTick(this.scheduleMobileAskDockUpdate);
            },
            askSources: {
                handler: 'persistHomeAskState',
                deep: true,
            },
            askError: 'persistHomeAskState',
            askStatus: 'persistHomeAskState',
            askMapVisible: 'persistHomeAskState',
            askMapHits: {
                handler: 'persistHomeAskState',
                deep: true,
            },
            viewOfferVisible: 'persistHomeAskState',
            homeAskEngaged() {
                this._mobileAskDockLastScrollY = null;
                this.$nextTick(this.scheduleMobileAskDockUpdate);
            },
        },
        created() {
            this.restoreHomeAskState();
            this.syncReturnViewState();
        },
        async mounted() {
            this._isUnmounted = false;
            if (isPhysicalPhoneBrowser()) {
                const navigation = window.performance?.getEntriesByType?.('navigation')?.[0];
                logEvent('home_mobile_motion_isolation', {
                    settled: this.settleRestoredMobileHome,
                    navigation_type: navigation?.type || null,
                    viewport: `${window.innerWidth}x${window.innerHeight}`,
                    screen: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
                    touch_points: navigator.maxTouchPoints || 0,
                    coarse_pointer: !!window.matchMedia?.('(pointer: coarse)').matches,
                });
            }
            this._askSeq = 0;
            this._viewSeq = 0;
            this._onHomeAskReady = this.startAskRotation;
            this._onHomeSceneChange = (event) => {
                this.homeScene = event.detail || null;
            };
            this._onHomeIntroStage = (event) => {
                const stage = Number(event.detail && event.detail.stage);
                if (Number.isFinite(stage)) this.homeIntroStage = stage;
            };
            window.addEventListener('home-ask-ready', this._onHomeAskReady);
            window.addEventListener('home-scene-change', this._onHomeSceneChange);
            window.addEventListener('home-intro-stage', this._onHomeIntroStage);
            // Scrolling away the pointer's anchor hides the hover preview
            // immediately, so it never floats at a stale position.
            this._onAnyScroll = () => {
                if (this.hoveredSourceId) this.hoveredSourceId = null;
                this.scheduleMobileAskDockUpdate();
                this.syncMobileHomeHeaderMaterial();
            };
            window.addEventListener('scroll', this._onAnyScroll, { capture: true, passive: true });
            this._onHomeResize = () => {
                this.scheduleMobileAskDockUpdate();
                this.syncHomeMotionForViewport();
                this.syncMobileHeroLayout();
                this.syncMobileHomeHeaderMaterial();
            };
            window.addEventListener('resize', this._onHomeResize, { passive: true });
            this.$nextTick(() => {
                // Prepare every below-the-fold mobile reveal before releasing
                // the homepage entrance. This ordering prevents a fast phone
                // from painting the finished section for one frame and then
                // receiving its hidden animation state.
                this.syncHomeMotionForViewport();
                this.startHomeEntrance();
                this.syncMobileHomeHeaderMaterial();
            });
            this.$nextTick(this.syncMobileHeroLayout);
            if (document.fonts && document.fonts.ready) {
                document.fonts.ready.then(() => this.syncMobileHeroLayout()).catch(() => {});
            }
            // Dynamically import the script when the component is mounted
            this.scriptModule = await import('../assets/js/home.js');
            if (this.scriptModule && this.scriptModule.initScript) {
                this.scriptModule.initScript();
            }
            // Restored ask state is compacted for storage (no source bodies);
            // refill them from the search index so hover previews work again.
            this.rehydrateAskSources();
            if (this.askAnswer) this.groundAiFollowUps(this._askSeq);
            this.$nextTick(this.scheduleMobileAskDockUpdate);
            schedulePublicViewsPreload();
        },
        unmounted() {
            this._isUnmounted = true;
            // Clean up the script when the component is unmounted
            if (this._homeAnims) {
                this._homeAnims.forEach((animation) => animation.stop());
                this._homeAnims.clear();
            }
            if (this.scriptModule && this.scriptModule.cleanupScript) {
                this.scriptModule.cleanupScript();
            }
            this.scriptModule = null; // Optionally, clear the reference to the module
            if (this._onHomeAskReady) window.removeEventListener('home-ask-ready', this._onHomeAskReady);
            if (this._onHomeSceneChange) window.removeEventListener('home-scene-change', this._onHomeSceneChange);
            if (this._onHomeIntroStage) window.removeEventListener('home-intro-stage', this._onHomeIntroStage);
            if (this._onAnyScroll) window.removeEventListener('scroll', this._onAnyScroll, { capture: true, passive: true });
            if (this._onHomeResize) window.removeEventListener('resize', this._onHomeResize);
            document.body.classList.remove('home-mobile-hero-past');
            if (this._mobileAskDockFrame) cancelAnimationFrame(this._mobileAskDockFrame);
            if (this._mobileRevealReadyFrame) cancelAnimationFrame(this._mobileRevealReadyFrame);
            if (this._mobileAskDockAnimation) this._mobileAskDockAnimation.stop();
            if (this._homeSectionObserver) this._homeSectionObserver.disconnect();
            if (this._homeEntranceTimers) {
                this._homeEntranceTimers.forEach((timer) => clearTimeout(timer));
                this._homeEntranceTimers.clear();
            }
            if (this.askRotate) clearInterval(this.askRotate);
            if (this._homeAskMapFinalTimer) clearTimeout(this._homeAskMapFinalTimer);
            if (this.askBusy) {
                this.askBusy = false;
                this.askStatus = this.askAnswer ? '' : 'Paused.';
                this.persistHomeAskState();
            }
        },
        methods: {
            homeEntranceDelay(callback, delay) {
                if (!this._homeEntranceTimers) this._homeEntranceTimers = new Set();
                const timer = window.setTimeout(() => {
                    this._homeEntranceTimers.delete(timer);
                    if (!this._isUnmounted) callback();
                }, delay);
                this._homeEntranceTimers.add(timer);
                return timer;
            },
            animateHomeEntranceElement(element, keyframes, options) {
                if (!element) return null;
                let animation;
                const suppliedOnComplete = options && options.onComplete;
                const cleanup = () => {
                    element.style.removeProperty('opacity');
                    element.style.removeProperty('transform');
                    element.style.removeProperty('transform-origin');
                    element.style.removeProperty('clip-path');
                    this.untrackHomeAnim(animation);
                    if (suppliedOnComplete) suppliedOnComplete();
                };
                animation = this.trackHomeAnim(animate(element, keyframes, {
                    ...options,
                    onComplete: cleanup,
                }));
                return animation;
            },
            typeHomeSubtitle(element) {
                if (!element) return;
                const text = 'I explore.\nI try to understand.\nI build from there.';
                element.replaceChildren();
                element.style.visibility = 'visible';
                element.style.opacity = '1';
                const characters = [...text].map((character) => {
                    const span = document.createElement('span');
                    span.className = 'typed';
                    span.textContent = character;
                    span.style.visibility = 'hidden';
                    element.appendChild(span);
                    return span;
                });
                const caret = document.createElement('span');
                caret.className = 'home-type-caret';
                caret.setAttribute('aria-hidden', 'true');
                element.appendChild(caret);
                let index = 0;
                const reveal = () => {
                    if (index >= characters.length) {
                        caret.classList.add('is-finished');
                        this.homeEntranceDelay(() => caret.remove(), 520);
                        return;
                    }
                    const span = characters[index];
                    span.style.visibility = 'visible';
                    caret.style.left = `${span.offsetLeft + span.offsetWidth}px`;
                    caret.style.top = `${span.offsetTop + Math.max(1, (span.offsetHeight - caret.offsetHeight) / 2)}px`;
                    const character = text[index];
                    index += 1;
                    // Read at a human, deliberate pace: punctuation and line
                    // breaks pause longer so each sentence settles.
                    const pause = /[.!?]/.test(character) ? 360 : (character === '\n' ? 80 : 30);
                    this.homeEntranceDelay(reveal, pause);
                };
                reveal();
            },
            startHomeEntrance() {
                const root = this.$el;
                if (!root) return;
                const reduceMotion = this.homeReduceMotion();
                const isMobile = window.innerWidth <= 760;
                const isMedium = window.innerWidth > 760 && window.innerWidth <= 1100;
                const heroSection = root.querySelector('.home-hero-section');
                const mediaPanel = isMedium ? heroSection : root.querySelector('.home-hero-media');
                const hero = root.querySelector('.home-hero-panel');
                const title = root.querySelector('#welcomeTitle');
                const subtitle = root.querySelector('#welcomeSubtitle');
                const mobileHeroNotes = [...root.querySelectorAll('.home-hero-mobile-scene > a')];
                const socialLinks = [...root.querySelectorAll('.home-hero-socials a')];
                const askPanel = root.querySelector('.home-ask-panel');
                const askEyebrow = root.querySelector('.home-ask-panel > .home-section-eyebrow');
                const focusLinks = [...root.querySelectorAll('.home-ask-focus a')];
                const askIntro = root.querySelector('.home-ask-intro');
                const askBar = root.querySelector('.home-ask-bar');
                const askResult = root.querySelector('.home-ask-result');
                const askReturn = root.querySelector('.home-ask-focus-return');
                const askParts = [...root.querySelectorAll('.home-ask-panel > .home-section-eyebrow, .home-ask-intro, .home-ask-bar, .home-ask-result')]
                    .filter((element) => !element.classList.contains('home-section-eyebrow'));
                const sideNotes = [root.querySelector('.home-scene'), root.querySelector('.home-current-panel')].filter(Boolean);

                if (this.settleRestoredMobileHome) {
                    this.homeEntranceReady = true;
                    this.homeCardsDrawing = false;
                    if (subtitle) subtitle.textContent = 'I explore.\nI try to understand.\nI build from there.';
                    this.$nextTick(this.startAskRotation);
                    return;
                }

                if (reduceMotion) {
                    this.homeEntranceReady = true;
                    this.homeCardsDrawing = false;
                    subtitle.textContent = 'I explore.\nI try to understand.\nI build from there.';
                    this.$nextTick(this.startAskRotation);
                    return;
                }

                [title, subtitle].filter(Boolean).forEach((element) => {
                    element.style.opacity = '0';
                });
                if (isMobile) {
                    if (hero) hero.style.opacity = '0';
                    if (hero) hero.style.transform = 'translateY(10px) scale(0.992)';
                    if (title) title.style.transform = 'translateY(18px)';
                    if (subtitle) subtitle.style.visibility = 'hidden';
                    mobileHeroNotes.forEach((element) => {
                        element.style.opacity = '0';
                        element.style.transform = 'translateY(16px) scale(0.97)';
                        element.style.transformOrigin = 'right center';
                    });
                } else {
                    // Desktop choreography: each solid card is pulled out from
                    // beneath the card before it. The surfaces move first with
                    // no fade, then their contents arrive after the stack has
                    // fully opened.
                    this.homeCardsDrawing = true;
                    if (mediaPanel) {
                        mediaPanel.style.opacity = '1';
                        mediaPanel.style.transform = 'translateX(-100%)';
                    }
                    if (hero && !isMedium) {
                        hero.style.opacity = '1';
                        hero.style.transform = 'translateX(-100%)';
                        hero.style.visibility = 'hidden';
                    }
                    if (title) title.style.transform = 'translateY(18px)';
                    if (subtitle) subtitle.style.visibility = 'hidden';
                    mobileHeroNotes.forEach((element) => {
                        element.style.opacity = '0';
                        element.style.transform = 'translateY(10px)';
                    });
                    socialLinks.forEach((element) => {
                        element.style.opacity = '0';
                        element.style.transform = 'translateY(10px)';
                    });
                    sideNotes.forEach((element) => {
                        element.style.opacity = '0';
                        element.style.transform = 'translateY(8px)';
                    });
                    if (askPanel) {
                        askPanel.style.opacity = '1';
                        if (isMedium) {
                            // The medium hero is translucent video. Revealing Ask
                            // inside its own final bounds prevents its surface and
                            // border from showing through the Henry card.
                            askPanel.style.clipPath = 'inset(0 100% 0 0)';
                            askPanel.style.transform = 'none';
                            askPanel.style.visibility = 'visible';
                        } else {
                            askPanel.style.transform = 'translateX(-100%)';
                            askPanel.style.visibility = 'hidden';
                        }
                    }
                    focusLinks.forEach((element) => {
                        element.style.opacity = '0';
                        element.style.transform = 'translateY(8px)';
                    });
                    [askIntro, askBar].filter(Boolean).forEach((element) => {
                        element.style.opacity = '0';
                        element.style.transform = 'translateY(10px)';
                    });
                    [askEyebrow, askResult].filter(Boolean).forEach((element) => {
                        element.style.opacity = '0';
                        element.style.transform = 'translateY(8px)';
                    });
                    if (askReturn && this.homeAskEngaged) {
                        // The engaged back control has its own delayed
                        // opacity/transform/visibility transition and stays
                        // visible above the panel. Neutralise both so it cannot
                        // appear while the Ask card is still entering.
                        askReturn.style.transition = 'none';
                        askReturn.style.visibility = 'hidden';
                        askReturn.style.opacity = '0';
                        askReturn.style.transform = 'translateY(-6px) scale(0.96)';
                    }
                }
                this.homeEntranceReady = true;

                this.homeEntranceDelay(() => {
                    if (isMobile) {
                        this.animateHomeEntranceElement(hero, {
                            opacity: [0, 1],
                            transform: ['translateY(10px) scale(0.992)', 'translateY(0px) scale(1)'],
                        }, { type: 'spring', bounce: 0, duration: 0.46 });
                    } else {
                        this.animateHomeEntranceElement(mediaPanel, {
                            transform: ['translateX(-100%)', 'translateX(0px)'],
                        }, { duration: 0.3, ease: [0.22, 1, 0.36, 1] });
                    }
                }, 25);
                this.homeEntranceDelay(() => {
                    if (!isMobile && !isMedium && hero) {
                        hero.style.visibility = 'visible';
                        this.animateHomeEntranceElement(hero, {
                            transform: ['translateX(-100%)', 'translateX(0px)'],
                        }, { duration: 0.32, ease: [0.22, 1, 0.36, 1] });
                    }
                }, 190);
                this.homeEntranceDelay(() => {
                    if (!isMobile && askPanel) {
                        askPanel.style.visibility = 'visible';
                        this.animateHomeEntranceElement(askPanel, isMedium ? {
                            clipPath: ['inset(0 100% 0 0)', 'inset(0 0% 0 0)'],
                        } : {
                            transform: ['translateX(-100%)', 'translateX(0px)'],
                        }, { duration: 0.34, ease: [0.22, 1, 0.36, 1] });
                    }
                }, 380);
                if (!isMobile) {
                    this.homeEntranceDelay(() => {
                        this.homeCardsDrawing = false;
                    }, 730);
                }
                this.homeEntranceDelay(() => {
                    this.animateHomeEntranceElement(title, {
                        opacity: [0, 1],
                        transform: ['translateY(18px)', 'translateY(0px)'],
                    }, { type: 'spring', bounce: 0, duration: 0.38 });
                }, isMobile ? 150 : 760);

                if (isMobile) {
                    mobileHeroNotes.forEach((element, index) => {
                        this.homeEntranceDelay(() => {
                            this.animateHomeEntranceElement(element, {
                                opacity: [0, 1],
                                transform: ['translateY(16px) scale(0.97)', 'translateY(0px) scale(1)'],
                            }, { type: 'spring', bounce: 0, duration: 0.62 });
                        }, 720 + (index * 90));
                    });
                    this.homeEntranceDelay(() => this.typeHomeSubtitle(subtitle), 390);
                } else {
                    mobileHeroNotes.forEach((element, index) => {
                        this.homeEntranceDelay(() => {
                            this.animateHomeEntranceElement(element, {
                                opacity: [0, 1],
                                transform: ['translateY(10px)', 'translateY(0px)'],
                            }, { type: 'spring', bounce: 0, duration: 0.34 });
                        }, 820 + (index * 60));
                    });
                    socialLinks.forEach((element, index) => {
                        this.homeEntranceDelay(() => {
                            this.animateHomeEntranceElement(element, {
                                opacity: [0, 1],
                                transform: ['translateY(10px)', 'translateY(0px)'],
                            }, { type: 'spring', bounce: 0, duration: 0.34 });
                        }, 860 + (index * 60));
                    });
                    focusLinks.forEach((element, index) => {
                        this.homeEntranceDelay(() => {
                            this.animateHomeEntranceElement(element, {
                                opacity: [0, 1],
                                transform: ['translateY(8px)', 'translateY(0px)'],
                            }, { type: 'spring', bounce: 0, duration: 0.32 });
                        }, 880 + (index * 70));
                    });
                    this.homeEntranceDelay(() => {
                        [askEyebrow, askIntro, askBar, askResult].filter(Boolean).forEach((element) => {
                            this.animateHomeEntranceElement(element, {
                                opacity: [0, 1],
                                transform: ['translateY(10px)', 'translateY(0px)'],
                            }, { type: 'spring', bounce: 0, duration: 0.36 });
                        });
                        if (askReturn && this.homeAskEngaged) {
                            askReturn.style.visibility = 'visible';
                            this.animateHomeEntranceElement(askReturn, {
                                opacity: [0, 1],
                                transform: ['translateY(-6px) scale(0.96)', 'translateY(0px) scale(1)'],
                            }, { type: 'spring', bounce: 0, duration: 0.36 });
                        }
                    }, 960);
                    this.homeEntranceDelay(() => this.typeHomeSubtitle(subtitle), 870);
                }

                if (!isMobile) {
                    sideNotes.forEach((element, index) => {
                        this.homeEntranceDelay(() => {
                            this.animateHomeEntranceElement(element, {
                                opacity: [0, 1],
                                transform: ['translateY(8px)', 'translateY(0px)'],
                            }, { type: 'spring', bounce: 0, duration: 0.42 });
                        }, 900 + (index * 75));
                    });
                    // The entrance animations can leave their final opacity and
                    // transform as inline styles, which would pin later CSS
                    // transitions (Ask intro collapse, eyebrow reveal). Release
                    // them once every staggered step has settled.
                    const settleTargets = [
                        mediaPanel, hero, title, askPanel, askEyebrow, askResult, askReturn,
                        ...askParts, ...mobileHeroNotes, ...socialLinks, ...focusLinks, ...sideNotes,
                    ].filter(Boolean);
                    this.homeEntranceDelay(() => {
                        settleTargets.forEach((element) => {
                            element.style.removeProperty('opacity');
                            element.style.removeProperty('transform');
                            element.style.removeProperty('clip-path');
                            element.style.removeProperty('visibility');
                        });
                        if (askReturn && this.homeAskEngaged) askReturn.style.removeProperty('transition');
                    }, 1500);
                }
                this.homeEntranceDelay(this.startAskRotation, isMobile ? 520 : 900);
            },
            clearMobileRevealStyles() {
                if (!this.$el) return;
                this.$el.querySelectorAll('[data-home-mobile-reveal]').forEach((element) => {
                    element.style.removeProperty('opacity');
                    element.style.removeProperty('transform');
                    element.removeAttribute('data-home-mobile-reveal');
                });
            },
            revealMobileSection(section, { instant = false } = {}) {
                if (!section || section.dataset.homeRevealed === 'true') return;
                section.dataset.homeRevealed = 'true';
                const children = [...section.querySelectorAll(':scope > .home-section-eyebrow, :scope > h2, .home-current-list > a, .home-current-cv, .home-ask-intro, .home-ask-result, .home-ask-bar-slot, .home-site-index nav > a')];
                if (instant) {
                    children.forEach((element) => {
                        element.style.removeProperty('opacity');
                        element.style.removeProperty('transform');
                        element.style.removeProperty('transform-origin');
                        element.removeAttribute('data-home-mobile-reveal');
                    });
                    return;
                }
                children.forEach((element, index) => {
                    this.homeEntranceDelay(() => {
                        const isHeading = element.matches('h2, .home-ask-intro');
                        const isEyebrow = element.classList.contains('home-section-eyebrow');
                        const isInput = element.classList.contains('home-ask-bar-slot');
                        const distance = isEyebrow ? 7 : (isInput ? 10 : 20);
                        const scale = isHeading ? 0.982 : (isEyebrow ? 1 : 0.992);
                        this.animateHomeEntranceElement(element, {
                            opacity: [0, 1],
                            transform: [
                                `translateY(${distance}px) scale(${scale})`,
                                'translateY(0px) scale(1)',
                            ],
                        }, {
                            type: 'spring',
                            bounce: 0,
                            duration: isHeading ? 0.52 : 0.42,
                        });
                    }, index * 54);
                });
            },
            setupMobileSectionReveals() {
                if (!this.$el) return;
                if (this.settleRestoredMobileHome) {
                    this.mobileRevealReady = true;
                    [
                        this.$el.querySelector('.home-current-panel'),
                        this.$el.querySelector('.home-ask-panel'),
                        this.$el.querySelector('.home-site-index'),
                    ].filter(Boolean).forEach((section) => {
                        section.dataset.homeRevealed = 'true';
                    });
                    return;
                }
                if (this.homeReduceMotion()) {
                    this.mobileRevealReady = true;
                    return;
                }
                const sections = [
                    this.$el.querySelector('.home-current-panel'),
                    this.$el.querySelector('.home-ask-panel'),
                    this.$el.querySelector('.home-site-index'),
                ].filter(Boolean);
                sections.forEach((section) => {
                    section.dataset.homeRevealed = 'false';
                    section.dataset.homeObserved = '';
                    const children = [...section.querySelectorAll(':scope > .home-section-eyebrow, :scope > h2, .home-current-list > a, .home-current-cv, .home-ask-intro, .home-ask-result, .home-ask-bar-slot, .home-site-index nav > a')];
                    children.forEach((element) => {
                        element.dataset.homeMobileReveal = '';
                        element.style.opacity = '0';
                        const isHeading = element.matches('h2, .home-ask-intro');
                        const isEyebrow = element.classList.contains('home-section-eyebrow');
                        const isInput = element.classList.contains('home-ask-bar-slot');
                        const distance = isEyebrow ? 7 : (isInput ? 10 : 20);
                        const scale = isHeading ? 0.982 : (isEyebrow ? 1 : 0.992);
                        element.style.transform = `translateY(${distance}px) scale(${scale})`;
                        element.style.transformOrigin = 'left center';
                    });
                });
                this._homeSectionObserver = new IntersectionObserver((entries) => {
                    entries.forEach((entry) => {
                        const section = entry.target;
                        const initialProbe = section.dataset.homeObserved !== '1';
                        section.dataset.homeObserved = '1';
                        if (!entry.isIntersecting) return;
                        // Phones restore the scroll position on back/forward or
                        // reload, so a section can already be inside the viewport
                        // when it is first observed. Reveal those instantly; only
                        // sections the visitor scrolls into later get the entrance
                        // animation.
                        this.revealMobileSection(section, { instant: initialProbe });
                        this._homeSectionObserver.unobserve(section);
                    });
                }, { threshold: 0.16, rootMargin: '0px 0px -10% 0px' });
                sections.forEach((section) => this._homeSectionObserver.observe(section));
                // The initial CSS curtain keeps these elements hidden before
                // mounted() runs. Release it only after every reveal target has
                // received its starting inline state, avoiding a visible-first
                // flash followed by a second entrance.
                this._mobileRevealReadyFrame = requestAnimationFrame(() => {
                    this._mobileRevealReadyFrame = 0;
                    if (!this._isUnmounted) this.mobileRevealReady = true;
                });
            },
            syncHomeMotionForViewport() {
                const mobile = window.innerWidth <= 760;
                this.mobileHomeViewport = window.innerWidth <= 1100;
                if (this._homeMotionMobile === mobile) return;
                this._homeMotionMobile = mobile;
                if (this._homeSectionObserver) {
                    this._homeSectionObserver.disconnect();
                    this._homeSectionObserver = null;
                }
                this.clearMobileRevealStyles();
                if (mobile) {
                    this.mobileRevealReady = this.settleRestoredMobileHome;
                    this.setupMobileSectionReveals();
                } else {
                    this.mobileRevealReady = true;
                }
            },
            syncMobileHomeHeaderMaterial() {
                if (this._isUnmounted || !this.$el || window.innerWidth > 760) {
                    document.body.classList.remove('home-mobile-hero-past');
                    return;
                }
                const hero = this.$el.querySelector('.home-hero-section');
                const header = document.querySelector('.header');
                if (!hero || !header) return;
                const heroBottom = hero.getBoundingClientRect().bottom;
                const headerBottom = header.getBoundingClientRect().bottom;
                // Keep the glass treatment while moving imagery still sits
                // beneath the header. The small overlap gives the material
                // transition time to settle before the next section is exposed.
                document.body.classList.toggle('home-mobile-hero-past', heroBottom <= headerBottom + 8);
            },
            syncMobileHeroLayout() {
                if (this._isUnmounted || !this.$el) return;
                if (window.innerWidth > 760) return;
                const panel = this.$el.querySelector('.home-hero-panel');
                const h1 = this.$el.querySelector('.home-hero-copy h1');
                const h2 = this.$el.querySelector('.home-hero-copy h2');
                const scene = this.$el.querySelector('.home-hero-mobile-scene');
                if (!panel || !h1 || !h2 || !scene) return;
                const h1Height = h1.getBoundingClientRect().height;
                const h2Style = window.getComputedStyle(h2);
                const h2Margin = parseFloat(h2Style.marginTop) || 0;
                // The subtitle is three fixed lines, so use line height instead
                // of the mid-typing DOM height.
                const h2Height = (parseFloat(h2Style.lineHeight) || 0) * 3;
                // Applied AI / HKU and the PwC line below it are one group for
                // the vertical rhythm, so measure the whole scene block.
                const sceneHeight = scene.getBoundingClientRect().height;
                const sectionHeight = panel.getBoundingClientRect().height;
                if (!h1Height || !h2Height || !sceneHeight || !sectionHeight) return;
                // d = (section - title - subtitle block - scene group) / (2 + 1.2)
                const d = Math.max(20, (sectionHeight - h1Height - h2Margin - h2Height - sceneHeight) / 3.2);
                panel.style.setProperty('--hero-gap', `${Math.round(d)}px`);
            },
            scheduleMobileAskDockUpdate() {
                if (this._mobileAskDockFrame) return;
                this._mobileAskDockFrame = requestAnimationFrame(() => {
                    this._mobileAskDockFrame = 0;
                    this.updateMobileAskDock();
                });
            },
            clearMobileAskDockPresentation(bar) {
                if (!bar) return;
                ['opacity', 'transform', 'scale', 'transform-origin', 'will-change'].forEach((property) => {
                    bar.style.removeProperty(property);
                });
            },
            stopMobileAskDockAnimation() {
                if (!this._mobileAskDockAnimation) return;
                this._mobileAskDockAnimation.stop();
                this._mobileAskDockAnimation = null;
            },
            animateMobileAskDockIn(bar, fromCurrent = false) {
                this.stopMobileAskDockAnimation();
                this._mobileAskDockExitingTop = false;
                if (this.homeReduceMotion()) {
                    this.clearMobileAskDockPresentation(bar);
                    return;
                }
                bar.style.transformOrigin = '50% 0%';
                bar.style.willChange = 'transform, opacity';
                const keyframes = fromCurrent
                    ? { opacity: 1, scale: 1 }
                    : { opacity: [0, 1], scale: [0.985, 1] };
                const animation = animate(bar, keyframes, {
                    duration: 0.24,
                    ease: [0.22, 1, 0.36, 1],
                });
                this._mobileAskDockAnimation = animation;
                animation.finished.then(() => {
                    if (this._mobileAskDockAnimation !== animation) return;
                    this._mobileAskDockAnimation = null;
                    this.clearMobileAskDockPresentation(bar);
                }).catch(() => {});
            },
            animateMobileAskDockOut(bar) {
                if (this._mobileAskDockExitingTop) return;
                this.stopMobileAskDockAnimation();
                if (this.homeReduceMotion()) {
                    this.mobileAskInputDocked = false;
                    this.clearMobileAskDockPresentation(bar);
                    return;
                }
                this._mobileAskDockExitingTop = true;
                bar.style.transformOrigin = '50% 0%';
                bar.style.willChange = 'transform, opacity';
                const animation = animate(bar, { opacity: 0, scale: 0.985 }, {
                    duration: 0.18,
                    ease: [0.4, 0, 1, 1],
                });
                this._mobileAskDockAnimation = animation;
                animation.finished.then(() => {
                    if (this._mobileAskDockAnimation !== animation || !this._mobileAskDockExitingTop) return;
                    this._mobileAskDockAnimation = null;
                    this._mobileAskDockExitingTop = false;
                    this.mobileAskInputDocked = false;
                    this.$nextTick(() => this.clearMobileAskDockPresentation(bar));
                }).catch(() => {});
            },
            updateMobileAskDock() {
                // The floating composer is retired: the input stays in the
                // document flow right below the "Ask the site" eyebrow and
                // above the answer, so no docking logic runs anymore.
                this.stopMobileAskDockAnimation();
                this._mobileAskDockExitingTop = false;
                this._mobileAskDockLastScrollY = null;
                this.mobileAskInputDocked = false;
            },
            homeReduceMotion() {
                return window.matchMedia
                    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            },
            trackHomeAnim(animation) {
                if (!this._homeAnims) this._homeAnims = new Set();
                this._homeAnims.add(animation);
                return animation;
            },
            untrackHomeAnim(animation) {
                if (this._homeAnims) this._homeAnims.delete(animation);
            },
            // Anchor the source preview to the hovered chip, like the palette:
            // float it just below the chip and flip above when the wrap has no
            // room, instead of always showing under the whole row.
            positionSourcePreview(id) {
                if (!id) {
                    this.previewStyle = null;
                    return;
                }
                this.$nextTick(() => {
                    const wrap = this.$el && this.$el.querySelector('.home-ask-sources-wrap');
                    const chip = wrap && wrap.querySelector(`.home-ask-source[data-source-key="${id}"]`);
                    const preview = document.querySelector('.home-ask-preview');
                    if (!wrap || !chip || !preview) return;
                    const cr = chip.getBoundingClientRect();
                    const gap = 8;
                    const width = Math.max(200, Math.min(340, window.innerWidth - 24));
                    const left = Math.max(12, Math.min(cr.left, window.innerWidth - width - 12));
                    const height = preview.offsetHeight || 150;
                    const vh = window.innerHeight;
                    let top;
                    if (cr.bottom + gap + height <= vh - 8) {
                        top = cr.bottom + gap;
                    } else if (cr.top - gap - height >= 8) {
                        top = cr.top - gap - height;
                    } else {
                        top = Math.max(8, vh - height - 8);
                    }
                    this.previewStyle = {
                        left: `${left}px`,
                        top: `${top}px`,
                        width: `${width}px`,
                    };
                });
            },
            // Same "forming" entrance as the search palette: the answer settles
            // in when the first tokens arrive, and cited sources cascade in.
            beforeHomeAskEnter(el) {
                el.style.opacity = '0';
                el.style.transform = 'translateY(6px)';
            },
            enterHomeAsk(el, done) {
                if (this.homeReduceMotion()) {
                    el.style.opacity = '';
                    el.style.transform = '';
                    done();
                    return;
                }
                let settled = false;
                const finish = () => {
                    if (settled) return;
                    settled = true;
                    el.style.opacity = '';
                    el.style.transform = '';
                    this.untrackHomeAnim(animation);
                    done();
                };
                const animation = this.trackHomeAnim(animate(el, {
                    opacity: [0, 1],
                    transform: ['translateY(6px)', 'translateY(0px)'],
                }, {
                    type: 'spring',
                    bounce: 0,
                    duration: 0.4,
                    onComplete: finish,
                }));
                window.setTimeout(finish, 1000);
            },
            leaveHomeAsk(el, done) {
                if (this.homeReduceMotion()) {
                    done();
                    return;
                }
                let settled = false;
                const finish = () => {
                    if (settled) return;
                    settled = true;
                    this.untrackHomeAnim(animation);
                    done();
                };
                const animation = this.trackHomeAnim(animate(el, {
                    opacity: [1, 0],
                    transform: ['translateY(0px)', 'translateY(-4px)'],
                }, {
                    type: 'spring',
                    bounce: 0,
                    duration: 0.18,
                    onComplete: finish,
                }));
                window.setTimeout(finish, 400);
            },
            beforeHomeChipEnter(el) {
                el.style.opacity = '0';
                el.style.transform = 'translateY(4px)';
            },
            enterHomeChip(el, done) {
                if (this.homeReduceMotion()) {
                    el.style.opacity = '';
                    el.style.transform = '';
                    done();
                    return;
                }
                const index = Math.max(0, Array.from(el.parentNode ? el.parentNode.children : []).indexOf(el));
                let settled = false;
                const finish = () => {
                    if (settled) return;
                    settled = true;
                    el.style.opacity = '';
                    el.style.transform = '';
                    this.untrackHomeAnim(animation);
                    done();
                };
                const animation = this.trackHomeAnim(animate(el, {
                    opacity: [0, 1],
                    transform: ['translateY(4px)', 'translateY(0px)'],
                }, {
                    type: 'spring',
                    bounce: 0,
                    duration: 0.3,
                    delay: index * 0.045,
                    onComplete: finish,
                }));
                window.setTimeout(finish, 1000);
            },
            leaveHomeChip(el, done) {
                if (this.homeReduceMotion()) {
                    done();
                    return;
                }
                let settled = false;
                const finish = () => {
                    if (settled) return;
                    settled = true;
                    this.untrackHomeAnim(animation);
                    done();
                };
                const animation = this.trackHomeAnim(animate(el, {
                    opacity: [1, 0],
                    transform: ['translateY(0px)', 'translateY(-2px)'],
                }, {
                    type: 'spring',
                    bounce: 0,
                    duration: 0.12,
                    onComplete: finish,
                }));
                window.setTimeout(finish, 400);
            },
            restoreHomeAskState() {
                // The root route (#/) is the fresh landing-home state. It should not
                // inherit a previously restored Ask/map result. Keep restoration only
                // for /home, which is the intentional return-from-another-tab state.
                if (this.$route.path === '/') return;

                const saved = readHomeAskState();
                if (!saved) return;
                this.askText = saved.askText || '';
                this.askAnswer = saved.askAnswer || '';
                this.askSources = Array.isArray(saved.askSources) ? saved.askSources : [];
                this.askError = saved.askError || '';
                this.askStatus = saved.askStatus || '';
                this.askMapVisible = !!saved.askMapVisible;
                this.askMapHits = Array.isArray(saved.askMapHits) ? saved.askMapHits : [];
                this.askMapLabel = saved.askMapLabel || '';
                this.askMapFinalized = !!saved.askMapFinalized;
                this.viewOfferVisible = saved.viewOfferVisible === undefined
                    ? askViewRouting(this.askAnswer) === 'offer'
                    : !!saved.viewOfferVisible;
            },
            persistHomeAskState() {
                writeHomeAskState({
                    askText: this.askText,
                    askAnswer: this.askAnswer,
                    askSources: compactAskSources(this.askSources),
                    askError: this.askError,
                    askStatus: this.askStatus,
                    askMapVisible: this.askMapVisible,
                    askMapHits: this.askMapHits,
                    askMapLabel: this.askMapLabel,
                    askMapFinalized: this.askMapFinalized,
                    viewOfferVisible: this.viewOfferVisible,
                });
            },
            startAskRotation() {
                if (this.askRotate) return;
                this.askRotate = setInterval(() => {
                    if (!this.askFocused && !this.askText) {
                        this.askQIndex = this.nextRandomAskIndex();
                    }
                }, 3600);
            },
            nextRandomAskIndex() {
                const count = this.askQuestions.length;
                if (count <= 1) return this.askQIndex < 0 ? 0 : this.askQIndex;

                if (this.askQIndex < 0) {
                    return Math.floor(Math.random() * count);
                }

                let next = Math.floor(Math.random() * (count - 1));
                if (next >= this.askQIndex) next += 1;
                return next;
            },
            resetHomeAsk() {
                // The corner return mark clears the conversation and restores
                // the untouched Ask panel: sample prompt and Current Focus back.
                this._askSeq += 1;
                if (this._viewAbortController) {
                    this._viewAbortController.abort();
                    this._viewAbortController = null;
                }
                this.askText = '';
                this.askAnswer = '';
                this.askSources = [];
                this.askBusy = false;
                this.askError = '';
                this.askStatus = '';
                this.askMapVisible = false;
                this.askMapHits = [];
                this.askMapLabel = '';
                this.askMapFinalized = false;
                this.groundedFollowUps = null;
                this.viewOfferVisible = false;
                this.returnViewAvailable = false;
                this.viewBusy = false;
                this.viewStartedAt = 0;
                this.viewError = '';
                this.hoveredSourceId = null;
                this.askQIndex = -1;
                this.persistHomeAskState();
                this.$nextTick(this.scheduleMobileAskDockUpdate);
            },
            focusAskInput() {
                // The whole strip between the Ask bar's two hairlines focuses
                // the input; only the submit arrow keeps its own action.
                const input = this.$refs.askInput;
                if (input && document.activeElement !== input) input.focus();
            },
            submitAsk(e) {
                if (this.askComposing || (e && e.isComposing)) return; // let IME commit first
                if (e) e.preventDefault();
                this.askHome(this.askText.trim());
            },
            askFollowUp(q) {
                const question = String(q || '').trim();
                if (!question) return;
                this.askText = question;
                this.askQIndex = -1;
                this.hoveredSourceId = null;
                logEvent('ask_followup_click', { question, surface: 'home' }, this._lastAskEventId || null);
                this.submitAsk();
            },
            async askHome(text) {
                const question = String(text || '').trim();
                if (!question || !askConfigured()) return;
                if (looksLikeViewBuildRequest(question)) {
                    this.startHomeViewBuild(question);
                    return;
                }
                const seq = ++this._askSeq;
                this.askBusy = true;
                this.askError = '';
                this.askAnswer = '';
                this.askSources = [];
                this.askMapVisible = true;
                this.askMapHits = [];
                this.askMapLabel = question;
                this.askMapFinalized = false;
                this.groundedFollowUps = null;
                this.viewOfferVisible = false;
                this.returnViewAvailable = false;
                this.$nextTick(() => this.prepareHomeAskMap(question, seq));

                const telemetry = newAskContext('home');
                this._lastAskEventId = telemetry.event_id;
                const startedAt = Date.now();
                let firstTokenAt = null;
                let trace = null;
                let answer = '';
                let sources = [];
                let payloadLength = 0;
                let payloadChars = 0;
                let failure = '';

                try {
                    this.askStatus = 'Thinking…';
                    const recentSourceIds = askSourceIdsInRecent(loadAskSession(), 18);
                    // Keep all regular site context, then add fragment chunks retrieved for this question.
                    const contexts = diversifyAskWritingContexts(
                        await allAskContexts(question),
                        question,
                        telemetry.event_id,
                        recentSourceIds,
                    );
                    trace = takeRetrievalTrace();
                    sources = contexts;
                    if (seq !== this._askSeq) return;
                    this.askSources = contexts;
                    const payload = contexts.map(c => ({
                        page: c.page,
                        title: sourceDisplayTitle(c),
                        text: cleanAskContextText(c.body),
                        source_id: c.source_id || c.chunk_id || String(c.id || ''),
                    }));
                    payloadLength = payload.length;
                    payloadChars = payload.reduce((sum, c) => sum + String(c.text || '').length, 0);
                    const stream = askStream(question, payload, {
                        meta: {
                            event_id: telemetry.event_id,
                            session_id: telemetry.session_id,
                            turn_index: telemetry.turn_index,
                            surface: telemetry.surface,
                            recent_source_ids: recentSourceIds,
                        },
                        onFirstToken: () => { firstTokenAt = Date.now(); },
                    });
                    for await (const tok of stream) {
                        if (seq !== this._askSeq) return;
                        answer += tok;
                        this.askAnswer += tok;
                    }
                    if (!askResponseIsComplete(answer)) throw new Error('incomplete ask response');
                    if (seq === this._askSeq) {
                        const routing = askViewRouting(answer);
                        if (routing === 'build') {
                            await this.startHomeViewBuild(question);
                            return;
                        }
                        this.viewOfferVisible = routing === 'offer';
                        addAskTurn(addAskTurn(loadAskSession(), {
                            role: 'user',
                            text: question,
                            source_ids: [],
                        }), {
                            role: 'assistant',
                            text: answer,
                            source_ids: citedSourcesFromAnswer(answer, sources)
                                .map((s) => s.source_id)
                                .filter(Boolean),
                        });
                        this.finishHomeAskMap();
                        // Vet the model's follow-ups against the site's own
                        // embeddings so the chips never suggest something the
                        // site cannot answer.
                        this.groundAiFollowUps(seq);
                        this.persistHomeAskState();
                        if (this._isUnmounted) {
                            window.dispatchEvent(new CustomEvent('site-ai-complete', {
                                detail: {
                                    kind: 'ask',
                                    label: question,
                                    originRoute: '/home',
                                    surface: 'home',
                                },
                            }));
                        }
                    }
                } catch (err) {
                    failure = String(err?.message || err).slice(0, 200);
                    if (seq === this._askSeq) {
                        this.askAnswer = '';
                        this.askSources = [];
                        this.askError = 'error';
                    }
                    console.error('home ask failed:', err);
                } finally {
                    if (seq === this._askSeq) this.askBusy = false;
                    const finishedAt = Date.now();
                    logAskClient(telemetry, {
                        question,
                        retrieval_ms: trace ? trace.retrieval_ms : null,
                        ttft_ms: firstTokenAt ? firstTokenAt - startedAt : null,
                        stream_ms: firstTokenAt ? finishedAt - firstTokenAt : null,
                        total_ms: finishedAt - startedAt,
                        answer_len: answer.length,
                        aborted: seq !== this._askSeq,
                        error: failure || null,
                        n_contexts_sent: payloadLength,
                        contexts_chars: payloadChars,
                        n_lex: trace ? trace.n_lex : null,
                        n_sem: trace ? trace.n_sem : null,
                        sem_top_score: trace ? trace.sem_top_score : null,
                        cited: answer
                            ? citedSourcesFromAnswer(answer, sources).map(s => ({
                                n: s.n,
                                chunk_id: s.chunk_id,
                                route: s.route,
                                anchor: s.anchor || null,
                                title: s.title || null,
                            }))
                            : [],
                        retrieval: trace ? trace.rows : [],
                    });
                }
            },
            createViewFromAnswer() {
                const question = String(this.askText || '').trim();
                if (!question) return;
                if (this.returnViewAvailable) {
                    logEvent('view_return_clicked', { surface: 'home' }, this._lastAskEventId || null);
                    this.$router.push({ path: '/view' }).catch(() => {});
                    return;
                }
                logEvent('view_offer_clicked', { surface: 'home' }, this._lastAskEventId || null);
                this.startHomeViewBuild(question);
            },
            syncReturnViewState() {
                const currentView = loadAskSession()?.currentView;
                const currentQuestion = cleanViewIntent(this.askText).toLowerCase();
                const viewQuestion = String(currentView?.question || '').trim().toLowerCase();
                this.returnViewAvailable = !isDevelopMode(this.askText)
                    && !!currentView
                    && !!currentQuestion
                    && currentQuestion === viewQuestion;
                if (this.returnViewAvailable && this.askAnswerText) this.viewOfferVisible = true;
            },
            async startHomeViewBuild(question) {
                const rawMessage = String(question || this.askText || '').trim();
                const message = cleanViewIntent(rawMessage);
                if (!message || this.viewBusy) return;
                // Site-wide lock: only one View may compose at a time across
                // the homepage, the palette, and the /views composer.
                if (isViewComposing()) {
                    window.dispatchEvent(new CustomEvent('open-site-view'));
                    return;
                }
                const seq = ++this._viewSeq;
                if (this._viewAbortController) this._viewAbortController.abort();
                const controller = new AbortController();
                this._viewAbortController = controller;
                this.viewError = '';
                this.askError = '';
                this.viewBusy = true;
                this.viewStartedAt = Date.now();
                beginViewCompose('home', this.viewStartedAt, message);
                try {
                    if (isDevelopMode(rawMessage)) {
                        const fakeJob = {
                            version: 1,
                            jobId: `develop-${Date.now()}-${seq}`,
                            status: 'composing',
                            message: DEVELOP_VIEW_LABEL,
                            surface: 'home',
                            originRoute: '/home',
                            startedAt: Date.now(),
                        };
                        window.dispatchEvent(new CustomEvent('site-view-job', { detail: fakeJob }));
                        await developViewDelay(controller.signal);
                        if (controller.signal.aborted || seq !== this._viewSeq) {
                            throw new DOMException('Aborted', 'AbortError');
                        }
                        window.dispatchEvent(new CustomEvent('site-view-job', {
                            detail: { ...fakeJob, status: 'complete', completedAt: Date.now(), payload: null },
                        }));
                        if (this._isUnmounted) {
                            window.dispatchEvent(new CustomEvent('site-ai-complete', {
                                detail: {
                                    kind: 'view',
                                    label: DEVELOP_VIEW_LABEL,
                                    originRoute: '/home',
                                    surface: 'home',
                                    route: DEVELOP_VIEW_ROUTE,
                                },
                            }));
                        } else {
                            await this.$router.push({ path: DEVELOP_VIEW_ROUTE }).catch(() => {});
                        }
                        return;
                    }
                    if (recentDuplicateViewQuestion(message)) {
                        // The exact same question was composed within the last
                        // three days: reuse the existing View page.
                        await this.$router.push('/view').catch(() => {});
                        return;
                    }
                    let viewSession = loadAskSession();
                    const conversation = recentAskTurns(viewSession, 6);
                    const sources = await candidatesForView({
                        message,
                        session: viewSession,
                        latestMessage: message,
                    });
                    if (controller.signal.aborted || seq !== this._viewSeq) throw new DOMException('Aborted', 'AbortError');
                    logEvent('view_build_requested', { surface: 'home' });
                    const response = await requestView({
                        message,
                        conversation,
                        sources,
                        meta: {
                            surface: 'home',
                            ask_offer_confirmed: true,
                            event_id: newId(),
                            session_id: sessionId(),
                            turn_index: viewSession && viewSession.turns ? viewSession.turns.length + 1 : 1,
                        },
                        signal: controller.signal,
                    });
                    if (controller.signal.aborted || seq !== this._viewSeq) throw new DOMException('Aborted', 'AbortError');
                    const sanitizedResponse = sanitizeViewResponse(response, sources);
                    const validation = validateViewResponse(sanitizedResponse, sources);
                    if (!validation.ok) throw new Error(validation.error || 'The composed view did not pass validation.');
                    if (sanitizedResponse.action !== 'replace_view') throw new Error('The composer did not return a complete view.');
                    const view = validation.view || sanitizedResponse.view || null;
                    if (!view) throw new Error('Composition returned no view.');

                    viewSession = addAskTurn(viewSession, { role: 'user', text: message, source_ids: [] });
                    viewSession = addAskTurn(viewSession, {
                        role: 'assistant',
                        text: response.message || 'View created.',
                        source_ids: collectViewSourceIds(view),
                    });
                    viewSession = setAskView(viewSession, view);
                    viewSession = setAskOrigin(viewSession, '/home');
                    this.returnViewAvailable = true;
                    this.viewOfferVisible = true;
                    this.persistHomeAskState();
                    logEvent('view_build_success', {
                        surface: 'home',
                        n_sources: collectViewSourceIds(view).length,
                        n_sections: view.sections ? view.sections.length : 0,
                    });
                    if (this._isUnmounted) {
                        window.dispatchEvent(new CustomEvent('site-ai-complete', {
                            detail: {
                                kind: 'view',
                                label: view.title || message,
                                originRoute: '/home',
                                surface: 'home',
                            },
                        }));
                    } else {
                        this.$router.push({ path: '/view' }).catch(() => {});
                    }
                } catch (err) {
                    if (err?.name !== 'AbortError' && seq === this._viewSeq) {
                        this.viewError = String(err?.message || err).slice(0, 240);
                        logEvent('view_build_error', {
                            surface: 'home',
                            error: String(err?.message || err).slice(0, 160),
                        });
                    }
                } finally {
                    if (seq === this._viewSeq) {
                        this.viewBusy = false;
                        this.viewStartedAt = 0;
                        this._viewAbortController = null;
                        endViewCompose('home');
                    }
                }
            },
            async prepareHomeAskMap(question, seq) {
                try {
                    const hits = await semanticTopK(question, 8);
                    if (seq !== this._askSeq) return;
                    this.askMapHits = hits.map((h) => ({ id: h.id, score: h.score }));
                    this.askMapLabel = question;
                    const map = this.$refs.homeAskMap;
                    if (map && map.showExternalHits) {
                        // While waiting for the answer: keep the full map in view and just
                        // light up the retrieved passages, no zoom-in yet.
                        map.showExternalHits(this.askMapHits, {
                            label: question,
                            focus: false,
                            keepCentered: false,
                            duration: 900,
                        });
                    }
                } catch (err) {
                    console.warn('home ask map failed:', err);
                }
            },
            citedSourceItems() {
                const cited = new Set();
                const re = /\[([\d,\s]+)\]/g;
                let m;
                while ((m = re.exec(this.askAnswer))) {
                    m[1].split(/[\s,]+/).filter(Boolean).forEach((n) => cited.add(parseInt(n, 10)));
                }
                return this.askSources.filter((source, i) => cited.has(i + 1) && source && source.id != null);
            },
            finishHomeAskMap() {
                const hits = this.citedSourceItems().map((source) => ({ id: source.id, score: 0.92 }));
                this.askMapHits = hits;
                this.askMapLabel = hits.length ? 'cited passages' : this.askMapLabel;
                this.askMapFinalized = true;
                const map = this.$refs.homeAskMap;
                if (!map || !map.showExternalHits) return;
                if (this._homeAskMapFinalTimer) clearTimeout(this._homeAskMapFinalTimer);
                // Answer is in: zoom in onto the cited passages. (With no citations the
                // hit list is empty and the map falls back to the full view.)
                map.showExternalHits(hits, {
                    label: this.askMapLabel,
                    focus: true,
                    keepCentered: true,
                    duration: 850,
                });
            },
            async rehydrateAskSources() {
                const missing = this.askSources.filter((s) => !s.body);
                if (!missing.length) return;
                try {
                    const items = await loadSearchItems();
                    const byKey = new Map();
                    for (const it of items) byKey.set(`${it.route}#${it.anchor || ''}`, it);
                    let changed = false;
                    const hydrated = this.askSources.map((s) => {
                        if (s.body) return s;
                        const it = byKey.get(`${s.route}#${s.anchor || ''}`);
                        if (!it) return s;
                        changed = true;
                        return { ...s, body: it.body, chunk_id: String(it.id) };
                    });
                    if (changed) {
                        this.askSources = hydrated;
                        this.persistHomeAskState();
                    }
                } catch (e) {
                    // Keep the fallback preview text; Ask itself still works.
                }
            },
            async groundAiFollowUps(seq) {
                const questions = askAnswerFollowUps(this.askAnswer);
                if (!questions.length) {
                    if (seq === this._askSeq) this.groundedFollowUps = [];
                    return;
                }
                const grounded = await groundAskFollowUps(questions);
                if (seq !== this._askSeq) return;
                this.groundedFollowUps = grounded;
            },
            syncHomeAskMap() {
                const map = this.$refs.homeAskMap;
                if (!map || !map.showExternalHits || !this.askMapHits.length) return;
                map.showExternalHits(this.askMapHits, {
                    label: this.askMapLabel,
                    // Full map until the answer is finalized, then focus on the cited passages.
                    focus: this.askMapFinalized,
                    keepCentered: this.askMapFinalized,
                    duration: 500,
                });
            },
            goSource(source) {
                if (!source) return;
                const destination = sourceRouteTarget(source.route, source.anchor);
                if (destination?.href) {
                    window.open(destination.href, '_blank', 'noopener');
                    return;
                }
                logEvent('ask_source_click', {
                    chunk_id: source.chunk_id || null,
                    route: source.route,
                    anchor: source.anchor || null,
                    title: source.title || null,
                    surface: 'home',
                }, this._lastAskEventId || null);
                const samePage = this.$route.path === source.route;

                if (!samePage) {
                    // Cross-page landings are handled centrally once the new
                    // page has mounted; keep the anchor in the URL so the
                    // landing knows where to go.
                    const target = { path: source.route };
                    if (source.anchor) target.hash = `#${source.anchor}`;
                    this.$router.push(target).catch(() => {});
                    return;
                }

                if (source.anchor) settleScrollToId(source.anchor);
                else window.scrollTo({ top: 0, behavior: 'smooth' });
            },
        },
    }
</script>
 
<style scoped>
.home-page {
  --home-hairline: rgba(60, 60, 67, 0.16);
  --home-muted: #76767c;
  --home-eyebrow: #4f4f54;
  --home-page-surface: #fff;
  --home-identity-surface: #111113;
  --home-identity-ink: #f5f5f7;
  --home-white-hand: url('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 448 512"%3E%3Cpath fill="%23FFFFFF" stroke="%23000000" stroke-width="5" d="M128 40c0-22.1 17.9-40 40-40s40 17.9 40 40l0 148.2c8.5-7.6 19.7-12.2 32-12.2c20.6 0 38.2 13 45 31.2c8.8-9.3 21.2-15.2 35-15.2c25.3 0 46 19.5 47.9 44.3c8.5-7.7 19.8-12.3 32.1-12.3c26.5 0 48 21.5 48 48l0 48 0 16 0 48c0 70.7-57.3 128-128 128l-16 0-64 0-.1 0-5.2 0c-5 0-9.9-.3-14.7-1c-55.3-5.6-106.2-34-140-79L8 336c-13.3-17.7-9.7-42.7 8-56s42.7-9.7 56 8l56 74.7L128 40zM240 304c0-8.8-7.2-16-16-16s-16 7.2-16 16l0 96c0 8.8 7.2 16 16 16s16-7.2 16-16l0-96zm48-16c-8.8 0-16 7.2-16 16l0 96c0 8.8 7.2 16 16 16s16-7.2 16-16l0-96c0-8.8-7.2-16-16-16zm80 16c0-8.8-7.2-16-16-16s-16 7.2-16 16l0 96c0 8.8 7.2 16 16 16s16-7.2 16-16l0-96z"/%3E%3C/svg%3E') 8 0, pointer;
  --home-black-hand: url('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 448 512"%3E%3Cpath fill="%23000000" stroke="%23FFFFFF" stroke-width="5" d="M128 40c0-22.1 17.9-40 40-40s40 17.9 40 40l0 148.2c8.5-7.6 19.7-12.2 32-12.2c20.6 0 38.2 13 45 31.2c8.8-9.3 21.2-15.2 35-15.2c25.3 0 46 19.5 47.9 44.3c8.5-7.7 19.8-12.3 32.1-12.3c26.5 0 48 21.5 48 48l0 48 0 16 0 48c0 70.7-57.3 128-128 128l-16 0-64 0-.1 0-5.2 0c-5 0-9.9-.3-14.7-1c-55.3-5.6-106.2-34-140-79L8 336c-13.3-17.7-9.7-42.7 8-56s42.7-9.7 56 8l56 74.7L128 40zM240 304c0-8.8-7.2-16-16-16s-16 7.2-16 16l0 96c0 8.8 7.2 16 16 16s16-7.2 16-16l0-96zm48-16c-8.8 0-16 7.2-16 16l0 96c0 8.8 7.2 16 16 16s16-7.2 16-16l0-96c0-8.8-7.2-16-16-16zm80 16c0-8.8-7.2-16-16-16s-16 7.2-16 16l0 96c0 8.8 7.2 16 16 16s16-7.2 16-16l0-96z"/%3E%3C/svg%3E') 8 0, pointer;
  display: grid;
  place-items: center;
  box-sizing: border-box;
  width: min(88rem, calc(100vw - var(--home-desktop-frame, 128px)));
  height: max(476px, calc(100dvh - 12.2rem));
  margin: 0 0 0 50%;
  transform: translate(-50%, -8px);
  color: #252527;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif;
}

.home-core {
  display: grid;
  grid-template-columns: minmax(205px, 0.25fr) minmax(285px, 0.31fr) minmax(400px, 0.44fr);
  gap: 0;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  transform: translateY(-4px);
}

.home-hero-section {
  display: contents;
}

.home-page.is-card-drawing .home-core {
  overflow: hidden;
}

.home-page.is-card-drawing .home-hero-media { z-index: 3; }
.home-page.is-card-drawing .home-hero-panel { z-index: 2; }
.home-page.is-card-drawing .home-ask-panel { z-index: 1; }

.home-page:not(.is-entrance-ready) .home-hero-media,
.home-page:not(.is-entrance-ready) .home-hero-panel,
.home-page:not(.is-entrance-ready) .home-ask-panel,
.home-page:not(.is-entrance-ready) .home-scene,
.home-page:not(.is-entrance-ready) .home-current-panel,
.home-page:not(.is-entrance-ready) .home-hero-mobile-scene {
  opacity: 0;
}

/* Current Focus explicitly restores visibility and its links transition opacity.
   Seed both states before the Ask card moves in, so setting the Motion start
   values cannot create a visible fade-out over the Henry card. */
.home-page:not(.is-entrance-ready) .home-ask-focus,
.home-page:not(.is-entrance-ready) .home-ask-focus a {
  opacity: 0;
  visibility: hidden;
  transition: none;
}

.home-hero-media,
.home-hero-panel,
.home-ask-panel,
.home-current-panel {
  min-width: 0;
  min-height: 0;
}

.home-hero-panel {
  --github-hover-color: #f5f5f7;
  --home-panel-point: url('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 320 512"%3E%3Cpath fill="%23FFFFFF" stroke="%23000000" stroke-width="5" d="M0 55.2L0 426c0 12.2 9.9 22 22 22c6.3 0 12.4-2.7 16.6-7.5L121.2 346l58.1 116.3c7.9 15.8 27.1 22.2 42.9 14.3s22.2-27.1 14.3-42.9L179.8 320l118.1 0c12.2 0 22.1-9.9 22.1-22.1c0-6.3-2.7-12.3-7.4-16.5L38.6 37.9C34.3 34.1 28.9 32 23.2 32C10.4 32 0 42.4 0 55.2z"/%3E%3C/svg%3E') 0 0, auto;
  position: relative;
  overflow: hidden;
  background: var(--home-identity-surface);
  cursor: var(--home-panel-point);
}

.home-hero-panel :deep(*) {
  cursor: inherit !important;
}

.home-hero-panel :deep(a),
.home-hero-panel :deep(button),
.home-hero-panel :deep([role="button"]) {
  cursor: var(--home-white-hand) !important;
}

.home-hero-media,
.home-hero-media video,
.home-hero-media .home-video-poster,
.home-hero-media .fallback-image,
.transition-overlay,
.description-overlay {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}
.home-hero-media {
  position: relative;
  display: block;
  overflow: hidden;
  background: #ececea;
  cursor: var(--home-white-hand) !important;
}

.home-hero-media video,
.home-hero-media .home-video-poster,
.home-hero-media .fallback-image {
  display: block;
  border-radius: 0;
  object-fit: cover;
  filter: none;
  background: #ececea;
  cursor: var(--home-white-hand) !important;
}

.home-hero-media video {
  opacity: 0;
  transition: opacity 280ms ease-out;
}

.home-hero-media.is-video-ready video {
  opacity: 1;
}

.home-video-poster {
  max-width: none;
  pointer-events: none;
}

.home-hero-media video:active {
  scale: 1;
}

.home-hero-media video:focus {
  outline: none;
}

.home-hero-media video:focus-visible {
  outline: none;
}

.home-hero-media:has(video:focus-visible) .description-overlay {
  opacity: 0.5;
}

.transition-overlay {
  inset: 0 -1px 0 0;
  width: auto;
  z-index: 4;
  display: grid;
  overflow: hidden;
  background: var(--home-identity-surface);
  color: var(--home-identity-ink);
  opacity: 0;
  place-items: center;
  pointer-events: none;
  visibility: hidden;
  transition:
    opacity 420ms cubic-bezier(0.32, 0.72, 0, 1),
    visibility 0s linear 420ms;
}
.transition-overlay.is-scene-transitioning {
  opacity: 1;
  visibility: visible;
  transition:
    opacity 420ms cubic-bezier(0.32, 0.72, 0, 1),
    visibility 0s linear 0s;
}
.transition-overlay span {
  color: inherit;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", system-ui, sans-serif;
  font-size: clamp(.94rem, 1.12vw, 1.12rem);
  font-weight: 520;
  letter-spacing: .08em;
  line-height: 1;
  opacity: 0;
  transform: rotate(-90deg) translateY(8px);
  transition:
    opacity 260ms ease,
    transform 520ms cubic-bezier(0.22, 1, 0.36, 1);
  white-space: nowrap;
}
.transition-overlay.is-scene-transitioning span {
  opacity: .82;
  transform: rotate(-90deg) translateY(0);
  transition-delay: 180ms;
}
.description-overlay {
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #111;
  color: white;
  opacity: 0;
  pointer-events: none;
}

.description-content {
  max-width: 24rem;
  padding: 2rem;
  text-align: center;
}

.description-content :deep(.home-video-sound-state) {
  display: grid;
  width: 2.5rem;
  height: 2.5rem;
  margin: 1rem auto 0;
  place-items: center;
  color: #f5f5f7;
  transform-origin: center;
}

.description-content :deep(.home-video-sound-state svg) {
  display: block;
  width: 100%;
  height: 100%;
  overflow: visible;
}

.description-content :deep(.home-video-sound-speaker) { fill: currentColor; }

.description-content :deep(.home-video-sound-wave),
.description-content :deep(.home-video-sound-slash) {
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.7;
  transform-origin: 12px 12px;
}

.description-content :deep(.home-video-sound-state.is-audible) {
  opacity: 0.92;
  animation: home-video-sound-on 420ms cubic-bezier(0.22, 1, 0.36, 1) both;
}

.description-content :deep(.home-video-sound-state.is-audible .home-video-sound-wave-near) {
  animation: home-video-wave-in 340ms 45ms cubic-bezier(0.22, 1, 0.36, 1) both;
}

.description-content :deep(.home-video-sound-state.is-audible .home-video-sound-wave-far) {
  animation: home-video-wave-in 380ms 95ms cubic-bezier(0.22, 1, 0.36, 1) both;
}

.description-content :deep(.home-video-sound-state.is-audible .home-video-sound-slash) { opacity: 0; }

.description-content :deep(.home-video-sound-state.is-muted) {
  opacity: 0.92;
  animation: home-video-sound-off 260ms cubic-bezier(0.32, 0.72, 0, 1) both;
}

.description-content :deep(.home-video-sound-state.is-muted .home-video-sound-wave) { opacity: 0; }
.description-content :deep(.home-video-sound-state.is-muted .home-video-sound-slash) { opacity: 1; }

@keyframes home-video-sound-on {
  from { opacity: 0.5; transform: scale(0.88); }
  to { opacity: 0.92; transform: scale(1); }
}

@keyframes home-video-sound-off {
  0% { opacity: 0.92; transform: scale(1); }
  45% { opacity: 0.92; transform: scale(0.9); }
  100% { opacity: 0.92; transform: scale(1); }
}

@keyframes home-video-wave-in {
  from { opacity: 0; transform: translateX(-1px) scale(0.78); }
  to { opacity: 1; transform: translateX(0) scale(1); }
}

.home-hero-copy {
  position: absolute;
  inset: 0;
  z-index: 4;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  box-sizing: border-box;
  height: auto !important;
  padding: clamp(30px, 4.5vh, 54px) clamp(24px, 3.2vw, 42px);
  background: var(--home-identity-surface);
  transform: none !important;
  pointer-events: none;
}

.home-hero-copy h1 {
  display: grid;
  width: min-content;
  margin: 0;
  color: #f5f5f7;
  font-family: "PT Serif", Georgia, serif;
  font-size: clamp(3.3rem, 5.1vw, 5.5rem) !important;
  font-weight: 400;
  letter-spacing: -0.055em;
  line-height: 0.9;
}

.home-hero-copy h1 > span { display: block; }
#compassContainer {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  opacity: 0;
}

.home-hero-copy h2 {
  position: relative;
  max-width: 24ch;
  margin: clamp(1.25rem, 3.5vh, 2.2rem) 0 0;
  color: #f5f5f7 !important;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif;
  font-size: clamp(1.15rem, 1.7vw, 1.5rem) !important;
  font-weight: 450;
  line-height: 1.7;
  text-align: left !important;
  text-align-last: left !important;
  white-space: pre-line !important;
}
:global(.home-hero-copy h2 .typed) {
  display: inline;
  color: inherit !important;
}
.home-type-caret {
  position: absolute;
  width: 1.5px;
  height: 1.05em;
  border-radius: 1px;
  background: currentColor;
  opacity: 0.88;
  animation: home-caret-blink 900ms steps(1, end) infinite;
  pointer-events: none;
}
.home-type-caret.is-finished { opacity: 0; transition: opacity 320ms ease; }
@keyframes home-caret-blink {
  0%, 48% { opacity: 0.88; }
  49%, 100% { opacity: 0; }
}

.home-hero-mobile-scene {
  position: absolute;
  right: clamp(22px, 2.3vw, 34px);
  bottom: clamp(24px, 4.2vh, 44px);
  z-index: 5;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.68rem;
  pointer-events: auto;
}
.home-hero-mobile-note,
.home-hero-mobile-pwc {
  display: flex;
  align-items: baseline;
  gap: 0.42rem;
  color: rgba(245, 245, 247, 0.62);
  font-family: "Quattrocento Sans", sans-serif;
  font-size: clamp(0.72rem, 0.82vw, 0.82rem);
  font-weight: 400;
  letter-spacing: 0.14em;
  line-height: 1.3;
  text-align: right;
  text-decoration: none;
  text-transform: uppercase;
  transition:
    color 220ms cubic-bezier(0.22, 1, 0.36, 1),
    transform 240ms cubic-bezier(0.22, 1, 0.36, 1);
}
.home-hero-mobile-note strong,
.home-hero-mobile-pwc-title {
  color: #f5f5f7;
  font-size: clamp(0.82rem, 0.94vw, 0.94rem);
  font-weight: 600;
  letter-spacing: 0.17em;
}
.home-hero-mobile-pwc-title { text-transform: none; }
@media (hover: hover) {
  .home-hero-mobile-note:hover,
  .home-hero-mobile-pwc:hover {
    color: #f5f5f7;
    transform: translateY(-1px);
  }
}

.home-hero-socials {
  position: absolute;
  left: clamp(20px, 2vw, 30px);
  bottom: clamp(22px, 4vh, 42px);
  z-index: 6;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  pointer-events: auto;
}
.home-hero-socials a {
  display: inline-flex;
  width: 1.85rem;
  height: 1.85rem;
  align-items: center;
  justify-content: center;
  color: rgba(245, 245, 247, 0.58);
  font-size: 1rem;
  text-decoration: none;
  transition:
    color 180ms ease,
    transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
}
.home-hero-socials a i { padding: 0; }
.home-hero-socials a:hover,
.home-hero-socials a:focus-visible { transform: translateX(2px); }
.home-hero-socials a:hover .fa-instagram,
.home-hero-socials a:focus-visible .fa-instagram { color: #e1306c; }
.home-hero-socials a:hover .fa-linkedin,
.home-hero-socials a:focus-visible .fa-linkedin { color: #0073a4; }
.home-hero-socials a:hover .fa-github,
.home-hero-socials a:focus-visible .fa-github { color: var(--github-hover-color); }
.home-hero-socials a:focus-visible {
  border-radius: 4px;
  outline: 1px solid currentColor;
  outline-offset: 1px;
}

.home-scene {
  grid-area: scene;
  align-self: start;
  justify-self: end;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin: 14px 44px 0 0;
}
.home-scene-note {
  width: 154px;
  margin-top: 10px;
  color: #68686d;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.8rem;
  letter-spacing: 0.035em;
  line-height: 1.5;
  text-align: right;
  text-decoration: none;
  transition: color 160ms ease, transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
}
.home-scene-note:hover,
.home-scene-note:focus-visible { color: #3f3f43; transform: translateY(-1px); }
.home-scene::before {
  display: block;
  width: 28px;
  margin: 0 0 12px auto;
  border-top: 1px solid currentColor;
  content: "";
  opacity: 0.28;
}
.home-scene-note span,
.home-scene-note strong { display: block; }
.home-scene-note span:first-child {
  font-size: 0.65rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  opacity: 0.78;
}
.home-scene-note strong {
  display: inline-block;
  margin: 4px 0 1px;
  color: #3f3f43;
  font-size: 0.86rem;
  font-weight: 500;
  letter-spacing: 0.075em;
  min-width: 30px;
  text-transform: uppercase;
}
.home-scene-pwc {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 3px;
  margin-top: 0;
  color: #68686d;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.8rem;
  line-height: 1.5;
  text-align: right;
  text-decoration: none;
  transition:
    color 240ms cubic-bezier(0.22, 1, 0.36, 1),
    transform 240ms cubic-bezier(0.22, 1, 0.36, 1);
}
.home-scene-pwc-label {
  font-size: 0.65rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  opacity: 0.78;
}
.home-scene-pwc-title {
  color: #3f3f43;
  display: inline-block;
  font-size: 0.86rem;
  font-weight: 500;
  letter-spacing: 0.075em;
  min-width: 30px;
  text-transform: none;
}
.home-scene-pwc:hover,
.home-scene-pwc:focus-visible { color: #202022; transform: translateY(-1px); }

.home-current-panel {
  display: none;
  grid-area: current;
  align-self: end;
  justify-self: start;
  width: 174px;
  margin: 0 0 18px 44px;
  padding: 0;
  color: #69696e;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.7rem;
  letter-spacing: 0.035em;
  line-height: 1.5;
}

.home-section-eyebrow {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  color: var(--home-eyebrow);
  font-size: 0.64rem;
  font-weight: 650;
  letter-spacing: 0.18em;
  line-height: 1;
  text-transform: uppercase;
}
.home-section-eyebrow i {
  width: 26px;
  border-top: 1px solid currentColor;
  opacity: 0.55;
}
.home-ask-panel > .home-section-eyebrow {
  opacity: 0;
  transform: translateY(-10px);
  visibility: hidden;
  transition:
    opacity 240ms ease,
    transform 300ms cubic-bezier(0.4, 0, 0.6, 1),
    visibility 0s linear 240ms;
}
.home-ask-panel.is-engaged > .home-section-eyebrow {
  opacity: 1;
  transform: translateY(0);
  visibility: visible;
  transition:
    opacity 300ms ease 80ms,
    transform 380ms cubic-bezier(0.22, 1, 0.36, 1),
    visibility 0s linear 0s;
}
.home-current-panel h2 {
  display: none;
}
.home-current-panel .home-section-eyebrow i { display: none; }
.home-current-panel .home-section-eyebrow {
  color: inherit;
  font-size: 0.58rem;
  font-weight: 400;
  letter-spacing: 0.14em;
  opacity: 0.78;
}
.home-current-panel::before {
  display: block;
  width: 28px;
  margin-bottom: 12px;
  border-top: 1px solid currentColor;
  content: "";
  opacity: 0.28;
}
.home-current-list {
  display: grid;
  gap: 13px;
  margin: 13px 0 17px;
}
.home-current-list a {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: baseline;
  gap: 0.7rem;
  padding: 0;
  border: 0;
  color: #5d5d5d;
  text-decoration: none;
  opacity: 0.82;
  transition:
    color 240ms cubic-bezier(0.22, 1, 0.36, 1),
    transform 240ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 240ms ease;
}
.home-current-list a:hover,
.home-current-list a:focus-visible {
  color: #000;
  opacity: 1;
  transform: translateX(4px);
}
.home-current-list a:active { transform: translateX(4px) scale(0.985); }
.home-current-number { display: none; }
.home-current-arrow {
  width: 0.85em;
  height: 0.85em;
  opacity: 0;
  transform: translateX(-5px);
  transition:
    opacity 240ms ease,
    transform 260ms cubic-bezier(0.22, 1, 0.36, 1);
}
.home-current-list a:hover .home-current-arrow,
.home-current-list a:focus-visible .home-current-arrow {
  opacity: 1;
  transform: translateX(0);
}
.home-current-copy { display: grid; gap: 0.18rem; }
.home-current-copy strong { color: #3f3f43; font-size: 0.71rem; font-weight: 500; line-height: 1.35; letter-spacing: 0.025em; }
.home-current-copy small { color: #68686d; font-size: 0.58rem; line-height: 1.4; letter-spacing: 0.07em; text-transform: uppercase; opacity: 0.78; }
.home-current-cv {
  display: inline-block;
  color: #5d5d5d;
  font-size: 0.6rem;
  letter-spacing: 0.1em;
  text-decoration: none;
  text-transform: uppercase;
  opacity: 0.72;
  transition: color 160ms ease, opacity 160ms ease, transform 180ms cubic-bezier(0.23, 1, 0.32, 1);
}
.home-current-cv:hover,
.home-current-cv:focus-visible { color: #222; opacity: 1; transform: translateX(2px); }

.home-ask-panel {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: auto minmax(0, 1fr);
  box-sizing: border-box;
  /* The Ask conversation moves up as a whole so the input bar's bottom line
     lands on the hero's PwC row bottom, which stays where it is. */
  padding: clamp(28px, 4vh, 44px) clamp(26px, 3vw, 42px) calc(clamp(24px, 4.2vh, 44px) - 1px);
  border: 1px solid var(--home-hairline);
  border-left: 0;
  background: rgba(255, 255, 255, 0.28);
}
.home-ask-focus {
  position: absolute;
  z-index: 3;
  /* The first Current Focus title shares the cap line of the Henry H: the hero
     copy's top padding plus the display type's ink offset (≈0.13em of the h1),
     minus the small label's own ink offset. */
  top: calc(clamp(30px, 4.5vh, 54px) + 0.13 * clamp(3.3rem, 5.1vw, 5.5rem) - 5.7px);
  right: clamp(26px, 3vw, 42px);
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.52rem;
  pointer-events: auto;
  transform: translateY(0) scale(1);
  transform-origin: 100% 0;
  visibility: visible;
  will-change: transform, opacity;
  transition:
    opacity 260ms ease-out,
    transform 440ms cubic-bezier(0.22, 1, 0.36, 1),
    visibility 0s linear 0s;
}
.home-ask-focus a {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  box-sizing: border-box;
  padding-left: 1.25rem;
  color: #6a6a70;
  text-align: right;
  text-decoration: none;
  transition:
    color 240ms cubic-bezier(0.22, 1, 0.36, 1),
    transform 240ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 240ms ease;
}
.home-ask-focus-copy {
  display: grid;
  gap: 0.12rem;
}
.home-ask-focus-copy strong {
  color: #3f3f43;
  font-size: 0.86rem;
  font-weight: 600;
  line-height: 1.3;
  letter-spacing: 0.015em;
}
.home-ask-focus-copy small {
  color: #8a8a90;
  font-size: 0.68rem;
  line-height: 1.3;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}
.home-ask-focus-arrow {
  position: absolute;
  left: 0;
  top: 50%;
  width: 0.82rem;
  height: 0.82rem;
  opacity: 0;
  transform: translateY(-50%) translateX(-4px);
  transition:
    opacity 220ms ease,
    transform 240ms cubic-bezier(0.22, 1, 0.36, 1);
}
@media (hover: hover) {
  .home-ask-focus a:hover,
  .home-ask-focus a:focus-visible {
    color: #171719;
    transform: translateX(3px);
  }
  .home-ask-focus a:hover .home-ask-focus-arrow,
  .home-ask-focus a:focus-visible .home-ask-focus-arrow {
    opacity: 0.9;
    transform: translateY(-50%) translateX(0);
  }
}
.home-ask-panel.is-engaged .home-ask-focus {
  opacity: 0;
  transform: translateY(-48px) scale(0.965);
  visibility: hidden;
  pointer-events: none;
  transition:
    opacity 260ms ease-out,
    transform 440ms cubic-bezier(0.22, 1, 0.36, 1),
    visibility 0s linear 440ms;
}
.home-ask-focus-return {
  position: absolute;
  z-index: 4;
  /* Sits on the same horizontal line as the "Ask the site" eyebrow: roughly
     half the height difference between the small label and the button. */
  top: calc(clamp(28px, 4vh, 44px) - 0.65px);
  right: clamp(26px, 3vw, 42px);
  display: inline-flex;
  align-items: center;
  gap: 0.42rem;
  padding: 0;
  border: 0;
  background: transparent;
  color: #77777d;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.62rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  line-height: 1;
  text-transform: uppercase;
  cursor: pointer;
  opacity: 0;
  transform: translateY(-6px) scale(0.96);
  visibility: hidden;
  pointer-events: none;
  transition:
    opacity 280ms ease 180ms,
    transform 360ms cubic-bezier(0.22, 1, 0.36, 1) 180ms,
    visibility 0s linear 640ms;
}
.home-ask-focus-return i {
  font-size: 0.72rem;
  line-height: 1;
  transition: transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
}
.home-ask-panel.is-engaged .home-ask-focus-return {
  opacity: 1;
  transform: translateY(0) scale(1);
  visibility: visible;
  pointer-events: auto;
  transition:
    opacity 300ms ease 240ms,
    transform 420ms cubic-bezier(0.22, 1, 0.36, 1) 240ms,
    visibility 0s linear 240ms;
}
@media (hover: hover) {
  .home-ask-focus-return:hover,
  .home-ask-focus-return:focus-visible {
    color: #222224;
  }
  .home-ask-focus-return:hover i,
  .home-ask-focus-return:focus-visible i {
    transform: translateX(-3px);
  }
}
.home-ask-focus-return:focus-visible {
  outline: 1px solid currentColor;
  outline-offset: 4px;
}
.home-ask-stage {
  display: flex;
  width: 100%;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  flex-direction: column;
  justify-content: center;
  padding-top: 18px;
}
.home-ask-intro {
  max-height: 18rem;
  flex: 0 0 auto;
  opacity: 1;
  visibility: visible;
  transition:
    max-height 440ms cubic-bezier(0.22, 1, 0.36, 1),
    margin 440ms cubic-bezier(0.22, 1, 0.36, 1),
    visibility 0s linear 0s;
}
.home-ask-intro h2 {
  max-width: 11ch;
  margin: clamp(1.5rem, 3.4vh, 2.2rem) 0 1.15rem;
  color: #202022;
  font-family: "PT Serif", Georgia, serif;
  font-size: clamp(2rem, 2.7vw, 2.75rem);
  font-weight: 400;
  letter-spacing: -0.045em;
  line-height: 0.98;
  transform: translateY(0);
  transition:
    margin 420ms cubic-bezier(0.22, 1, 0.36, 1),
    font-size 420ms cubic-bezier(0.22, 1, 0.36, 1),
    line-height 420ms cubic-bezier(0.22, 1, 0.36, 1),
    transform 440ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 260ms ease-out;
}
.home-ask-title-wide { display: block; white-space: nowrap; }
.home-ask-title-mobile {
  display: none;
  /* Keep the mobile title's natural wrapping so its max-content width cannot
     collapse the Ask panel's grid column into a narrow centred strip. */
  white-space: normal;
}
.home-ask-intro p {
  max-width: 31ch;
  margin: 0;
  color: var(--home-muted);
  font-size: 0.82rem;
  line-height: 1.55;
  transform: translateY(0);
  transition:
    font-size 420ms cubic-bezier(0.22, 1, 0.36, 1),
    line-height 420ms cubic-bezier(0.22, 1, 0.36, 1),
    transform 440ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 260ms ease-out;
}

.home-ask {
  display: flex;
  width: 100%;
  min-width: 0;
  flex: 0 0 auto;
  flex-direction: column;
  min-height: 0;
  margin-top: clamp(1rem, 3vh, 2rem);
  border: 0;
  background: transparent;
  box-shadow: none;
  color: #2a2a2d !important;
  transition: margin 420ms cubic-bezier(0.22, 1, 0.36, 1);
}
.home-ask-panel.is-engaged .home-ask-stage { justify-content: flex-start; }
.home-ask-panel.is-engaged .home-ask-intro {
  max-height: 0;
  margin: 0;
  overflow: hidden;
  visibility: hidden;
  pointer-events: none;
  transition:
    max-height 440ms cubic-bezier(0.22, 1, 0.36, 1),
    margin 440ms cubic-bezier(0.22, 1, 0.36, 1),
    visibility 0s linear 440ms;
}
.home-ask-panel.is-engaged .home-ask-intro h2 {
  opacity: 0;
  transform: translateY(28px);
}
.home-ask-panel.is-engaged .home-ask-intro p {
  opacity: 0;
  transform: translateY(20px);
}
.home-ask-panel.is-engaged .home-ask {
  flex: 1 1 auto;
  margin-top: 0.55rem;
}
.home-ask-result {
  order: 1;
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  container-type: inline-size;
  margin: 0 0 0.8rem;
  padding-right: 0.35rem;
  overflow-y: auto;
  overscroll-behavior-y: auto;
  color: inherit;
  font-family: "PT Serif", Georgia, serif;
  font-size: 0.84rem;
  line-height: 1.5;
  scrollbar-width: thin;
}
.home-ask-result-busy {
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.home-ask-bar-slot {
  order: 2;
  flex: 0 0 auto;
  min-height: 48px;
}
.home-ask-bar {
  --home-ask-line-color: rgba(60, 60, 67, 0.25);
  --home-ask-line-offset: 0px;
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex: 0 0 auto;
  box-sizing: border-box;
  width: 100%;
  min-height: 48px;
  padding: 0.25rem 0;
  border: 0;
  cursor: var(--site-text-cursor);
}
.home-ask-bar::before,
.home-ask-bar::after {
  content: "";
  position: absolute;
  z-index: 0;
  right: 0;
  left: 0;
  height: 1px;
  background: var(--home-ask-line-color);
  pointer-events: none;
  transition:
    transform 300ms cubic-bezier(0.22, 1, 0.36, 1),
    background-color 180ms ease;
  will-change: transform;
}
.home-ask-bar::before {
  top: 1px;
  transform: translateY(calc(-1 * var(--home-ask-line-offset)));
}
.home-ask-bar::after {
  bottom: 1px;
  transform: translateY(var(--home-ask-line-offset));
}
@media (hover: hover) and (pointer: fine) {
  .home-ask-bar:hover { --home-ask-line-offset: 1px; }
}
.home-ask-bar:focus-within {
  --home-ask-line-color: rgba(29, 29, 31, 0.72);
  --home-ask-line-offset: 1px;
}
.home-ask-bar > * { position: relative; z-index: 1; }
.home-ask-spark { display: none; }
.home-ask-input-wrap { position: relative; display: flex; flex: 1; min-width: 0; align-items: center; }
.home-ask-input {
  width: 100%;
  border: 0;
  outline: 0;
  background: transparent;
  color: inherit;
  font: 0.8rem/1.4 -apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif;
  user-select: text;
}
.home-ask-prompt {
  position: absolute;
  inset-inline: 0;
  top: 50%;
  overflow: hidden;
  color: #8b8b90;
  font: 0.8rem/1.4 -apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif;
  text-overflow: ellipsis;
  white-space: nowrap;
  transform: translateY(-50%);
  pointer-events: none;
}
.home-ask-submit {
  appearance: none;
  padding: 0.25rem 0.5rem 0.25rem 0.6rem;
  border: 0;
  background: transparent;
  color: #85858a;
  font-size: 1.35rem;
  line-height: 1;
  cursor: pointer;
  transition:
    transform 160ms ease,
    color 160ms ease;
}
.home-ask-submit:hover { color: #252527; transform: translateX(2px); }
.home-ask-submit:active { transform: translateX(2px) scale(0.94); }
.home-ask-submit:focus-visible {
  color: #252527;
  outline: 1px solid currentColor;
  outline-offset: 2px;
}
.home-prompt-fade-enter-active,
.home-prompt-fade-leave-active { transition: opacity 320ms ease; }
.home-prompt-fade-enter-active { transition: opacity 320ms ease 160ms; }
.home-prompt-fade-enter-from,
.home-prompt-fade-leave-to { opacity: 0; }

.home-ask-answer,
.home-ask-status { margin: 0; color: inherit; opacity: 0.84; }
.home-ask-answer a.ask-inline-link { color: var(--site-accent-strong); text-underline-offset: 2px; }
.home-ask-map-shell { height: 190px; margin-top: 0.8rem; overflow: hidden; }
.home-ask-map,
.home-ask-map :deep(.emap) { height: 100%; min-height: 0; }
.home-ask-map :deep(.emap) { display: flex; flex-direction: column; }
.home-ask-map :deep(.emap-stage) { flex: 1 1 auto; min-height: 0; border: 0; border-radius: 0; background: transparent; }
.home-ask-sources-label,
.home-ask-followups-label {
  display: block;
  margin: 0.8rem 0 0.4rem;
  color: var(--home-muted);
  font: 600 0.58rem/1.2 -apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}
.home-ask-sources,
.home-ask-followups-list { display: flex; min-width: 0; flex-wrap: wrap; gap: 0.38rem; }
.home-ask-source,
.home-ask-followup {
  appearance: none;
  min-width: 0;
  max-width: 100%;
  padding: 0.38rem 0.5rem;
  overflow: hidden;
  border: 0;
  border-radius: 0;
  background: rgba(0, 0, 0, 0.055);
  color: #66666b;
  font: 0.66rem/1.35 -apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
  transition: color 160ms ease, background-color 160ms ease;
}
.home-ask-source:hover,
.home-ask-source:focus-visible,
.home-ask-followup:hover,
.home-ask-followup:focus-visible { color: #232326; background: rgba(0, 0, 0, 0.09); }
.home-ask-sources-wrap { position: relative; }
.home-ask-preview {
  position: fixed;
  z-index: 9100;
  box-sizing: border-box;
  padding: 0.65rem 0.75rem;
  border: 1px solid var(--home-hairline);
  background: rgba(250, 250, 251, 0.96);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.1);
  pointer-events: none;
}
.home-ask-preview-kicker { display: block; overflow: hidden; color: var(--home-muted); font: 600 0.56rem/1.2 sans-serif; letter-spacing: 0.1em; text-overflow: ellipsis; text-transform: uppercase; white-space: nowrap; }
.home-ask-preview-text { margin: 0.25rem 0 0; font-size: 0.72rem; line-height: 1.4; }
.home-view-offer { display: grid; gap: 0.7rem; margin-top: 0.9rem; padding: 0.8rem; background: rgba(var(--site-accent-rgb), 0.08); }
.home-view-offer-copy { display: grid; gap: 0.2rem; font-family: -apple-system, BlinkMacSystemFont, sans-serif; }
.home-view-offer-copy strong { color: var(--site-accent-ink); font-size: 0.76rem; }
.home-view-offer-copy span { color: var(--site-accent-muted); font-size: 0.66rem; line-height: 1.4; }
.home-view-offer-button { justify-self: start; padding: 0.4rem 0.55rem; border: 0; background: rgba(var(--site-accent-rgb), 0.12); color: var(--site-accent-strong); font: 600 0.66rem/1.3 sans-serif; cursor: pointer; }
.home-view-offer-button:disabled { opacity: 0.45; cursor: default; }

.home-site-index { display: none; }
#overlay { display: none; }

:global(html.dark-mode .home-page),
:global(body.dark-mode .home-page) {
  --home-hairline: rgba(255, 255, 255, 0.15);
  --home-muted: var(--site-text-secondary);
  --home-eyebrow: var(--site-text-secondary);
  --home-page-surface: #1c1c1e;
  --home-identity-surface: #f2f2f4;
  --home-identity-ink: #171719;
  color: #ececef;
}
:global(body.dark-mode .home-hero-panel) { background: var(--home-identity-surface); }
:global(body.dark-mode .home-hero-copy) { background: var(--home-identity-surface); }
:global(body.dark-mode .home-hero-copy h1),
:global(body.dark-mode .home-hero-copy h2) { color: #171719 !important; }
:global(body.dark-mode .home-hero-mobile-note),
:global(body.dark-mode .home-hero-mobile-pwc) { color: rgba(23, 23, 25, 0.58); }
:global(body.dark-mode .home-hero-mobile-note strong),
:global(body.dark-mode .home-hero-mobile-pwc-title) { color: #171719; }
@media (hover: hover) and (pointer: fine) and (min-width: 761px) {
  :global(body.dark-mode .home-hero-mobile-note:hover),
  :global(body.dark-mode .home-hero-mobile-pwc:hover) { color: #171719; }
}
:global(body.dark-mode .home-hero-panel) { --github-hover-color: #171719; }
:global(body.dark-mode .home-hero-socials a) { color: rgba(23, 23, 25, 0.58); }
:global(body.dark-mode .home-hero-panel) {
  --home-panel-point: url('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 320 512"%3E%3Cpath fill="%23000000" stroke="%23FFFFFF" stroke-width="5" d="M0 55.2L0 426c0 12.2 9.9 22 22 22c6.3 0 12.4-2.7 16.6-7.5L121.2 346l58.1 116.3c7.9 15.8 27.1 22.2 42.9 14.3s22.2-27.1 14.3-42.9L179.8 320l118.1 0c12.2 0 22.1-9.9 22.1-22.1c0-6.3-2.7-12.3-7.4-16.5L38.6 37.9C34.3 34.1 28.9 32 23.2 32C10.4 32 0 42.4 0 55.2z"/%3E%3C/svg%3E') 0 0, auto;
}
:global(body.dark-mode .home-hero-panel a),
:global(body.dark-mode .home-hero-panel button),
:global(body.dark-mode .home-hero-panel [role="button"]) {
  cursor: var(--home-black-hand) !important;
}
:global(body.dark-mode .home-scene-note) { color: var(--site-text-secondary); }
:global(body.dark-mode .home-scene-note span:first-child),
:global(body.dark-mode .home-scene-pwc-label),
:global(body.dark-mode .home-current-copy small),
:global(body.dark-mode .home-current-panel .home-section-eyebrow) {
  color: var(--site-text-tertiary);
  opacity: 1;
}
:global(body.dark-mode .home-scene-note strong) { color: #dedee1; }
:global(body.dark-mode .home-scene-note:hover),
:global(body.dark-mode .home-scene-note:focus-visible) { color: #f0f0f2; }
:global(body.dark-mode .home-scene-pwc) { color: var(--site-text-secondary); }
:global(body.dark-mode .home-scene-pwc-title) { color: #dedee1; }
:global(body.dark-mode .home-scene-pwc:hover),
:global(body.dark-mode .home-scene-pwc:focus-visible) { color: #e6e6e9; }
:global(body.dark-mode .home-current-panel) { color: var(--site-text-secondary); }
:global(body.dark-mode .home-current-copy strong),
:global(body.dark-mode .home-current-list a),
:global(body.dark-mode .home-current-cv) { color: #c8c8cc; }
:global(body.dark-mode .home-current-copy strong) { color: #dedee1; }
:global(body.dark-mode .home-current-copy small) { color: var(--site-text-tertiary); }
:global(body.dark-mode .home-current-list a:hover),
:global(body.dark-mode .home-current-cv:hover) { color: #e0e0e0; }
:global(body.dark-mode .home-ask-panel) { background: rgba(255, 255, 255, 0.025); }
:global(body.dark-mode .home-ask-intro h2),
:global(body.dark-mode .home-ask) { color: #eeeef0 !important; }
:global(body.dark-mode .home-ask-focus a) { color: rgba(238, 238, 240, 0.58); }
:global(body.dark-mode .home-ask-focus-copy strong) { color: #e8e8ed; }
:global(body.dark-mode .home-ask-focus-copy small) { color: rgba(238, 238, 240, 0.46); }
:global(body.dark-mode .home-ask-focus a:hover),
:global(body.dark-mode .home-ask-focus a:focus-visible) { color: #fff; }
:global(body.dark-mode .home-ask-focus-return) { color: #a1a1a6; }
:global(body.dark-mode .home-ask-focus-return:hover),
:global(body.dark-mode .home-ask-focus-return:focus-visible) { color: #f5f5f7; }
:global(body.dark-mode .home-ask-bar) { --home-ask-line-color: rgba(255, 255, 255, 0.22); }
:global(body.dark-mode .home-ask-bar:focus-within) { --home-ask-line-color: rgba(245, 245, 247, 0.76); }
:global(body.dark-mode .home-ask-submit:hover),
:global(body.dark-mode .home-ask-submit:focus-visible) { color: #f5f5f7; }
:global(body.dark-mode .home-ask-source),
:global(body.dark-mode .home-ask-followup) { color: var(--site-text-secondary); background: rgba(255, 255, 255, 0.08); }
:global(body.dark-mode .home-ask-source:hover),
:global(body.dark-mode .home-ask-followup:hover) { color: #fff; background: rgba(255, 255, 255, 0.13); }
:global(body.dark-mode .home-ask-preview) { background: rgba(44, 44, 46, 0.97); }

@media (min-width: 1101px) {
  .home-ask-focus-copy strong {
    font-size: 0.92rem;
  }
  .home-ask-focus-copy small {
    font-size: 0.71rem;
  }
  .home-ask-stage {
    justify-content: flex-end;
    padding-top: 34px;
  }
}

@media (max-width: 1100px) and (min-width: 761px) {
  .home-page {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas: "core";
    place-items: center;
    width: 100%;
    height: max(436px, calc(100dvh - 12.2rem));
  }
  .home-core {
    width: min(var(--site-content-width), calc(100vw - 48px));
    height: min(620px, 100%);
    grid-template-columns: minmax(220px, 0.46fr) minmax(300px, 0.54fr);
  }
  .home-hero-section {
    position: relative;
    display: block;
    min-width: 0;
    min-height: 0;
    overflow: hidden;
  }
  .home-hero-media {
    position: absolute;
    inset: 0;
    z-index: 1;
    display: block;
    width: 100%;
    height: 100%;
    background: #111113;
  }
  .home-hero-media video,
  .home-hero-media .home-video-poster,
  .home-hero-media .fallback-image {
    inset: -1px;
    width: calc(100% + 2px);
    height: calc(100% + 2px);
    object-fit: cover;
  }
  .description-overlay { display: none; }
  .transition-overlay { display: none; }
  .home-hero-panel {
    position: absolute;
    inset: -1px;
    z-index: 2;
    width: auto;
    height: auto;
    background: linear-gradient(180deg, rgba(8, 8, 10, 0.54) 0%, rgba(8, 8, 10, 0.24) 46%, rgba(8, 8, 10, 0.62) 100%);
  }
  .home-hero-copy { background: transparent; }
  .home-hero-copy h1,
  .home-hero-copy h2 { color: #f5f5f7 !important; }
  .home-hero-mobile-note,
  .home-hero-mobile-pwc { color: rgba(245, 245, 247, 0.68); }
  .home-hero-mobile-note strong,
  .home-hero-mobile-pwc-title { color: #f5f5f7; }
  .home-hero-socials a { color: rgba(245, 245, 247, 0.58); }
  .home-page.is-card-drawing .home-hero-media { z-index: 1; }
  .home-page.is-card-drawing .home-hero-panel { z-index: 2; }
  :global(body.dark-mode .home-hero-panel) {
    background: linear-gradient(180deg, rgba(8, 8, 10, 0.54) 0%, rgba(8, 8, 10, 0.24) 46%, rgba(8, 8, 10, 0.62) 100%);
  }
  :global(body.dark-mode .home-hero-copy) { background: transparent; }
  :global(body.dark-mode .home-hero-copy h1),
  :global(body.dark-mode .home-hero-copy h2) { color: #f5f5f7 !important; }
  :global(body.dark-mode .home-hero-mobile-note),
  :global(body.dark-mode .home-hero-mobile-pwc) { color: rgba(245, 245, 247, 0.68); }
  :global(body.dark-mode .home-hero-mobile-note strong),
  :global(body.dark-mode .home-hero-mobile-pwc-title) { color: #f5f5f7; }
  :global(body.dark-mode .home-hero-socials a) { color: rgba(245, 245, 247, 0.58); }
  @media (hover: hover) and (pointer: fine) {
    :global(body.dark-mode .home-hero-mobile-note:hover),
    :global(body.dark-mode .home-hero-mobile-pwc:hover) {
      color: #f5f5f7;
    }
  }
  :global(body.dark-mode .home-hero-panel a),
  :global(body.dark-mode .home-hero-panel button),
  :global(body.dark-mode .home-hero-panel [role="button"]) {
    cursor: var(--home-white-hand) !important;
  }
  .home-scene,
  .home-current-panel { display: none; }
  .home-ask-panel { padding-inline: 24px; }
  .home-ask-focus,
  .home-ask-focus-return {
    right: 24px;
  }
  /* With the side notes gone, the Ask conversation anchors to the bottom:
     the input stays put while the introduction slides up on engagement. */
  .home-ask-stage {
    justify-content: flex-end;
  }
}

/* On medium and large screens the View generation card reads as a centred
   card inside the Ask result area instead of a full-width strip. */
@media (min-width: 761px) {
  .home-ask-result .compose-progress:not(.compose-progress-compact) {
    width: min(100%, 34rem);
    margin-inline: auto;
  }
}

@media (max-width: 760px) {
  .home-page:not(.is-mobile-reveal-ready) .home-current-panel > .home-section-eyebrow,
  .home-page:not(.is-mobile-reveal-ready) .home-current-panel > h2,
  .home-page:not(.is-mobile-reveal-ready) .home-current-list > a,
  .home-page:not(.is-mobile-reveal-ready) .home-current-cv,
  .home-page:not(.is-mobile-reveal-ready) .home-ask-panel > .home-section-eyebrow,
  .home-page:not(.is-mobile-reveal-ready) .home-ask-intro,
  .home-page:not(.is-mobile-reveal-ready) .home-ask-result,
  .home-page:not(.is-mobile-reveal-ready) .home-ask-bar-slot,
  .home-page:not(.is-mobile-reveal-ready) .home-site-index > .home-section-eyebrow,
  .home-page:not(.is-mobile-reveal-ready) .home-site-index nav > a {
    opacity: 0;
    visibility: hidden;
  }
  .home-ask-focus,
  .home-ask-focus-return { display: none; }
  .home-page {
    display: flex;
    flex-direction: column;
    width: 100vw;
    height: auto;
    margin: calc(-48px - 0.82rem) 0 0 calc(50% - 50vw);
    transform: none;
  }

  .home-core { display: contents; }
  .home-hero-section {
    --home-panel-point: url('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 320 512"%3E%3Cpath fill="%23FFFFFF" stroke="%23000000" stroke-width="5" d="M0 55.2L0 426c0 12.2 9.9 22 22 22c6.3 0 12.4-2.7 16.6-7.5L121.2 346l58.1 116.3c7.9 15.8 27.1 22.2 42.9 14.3s22.2-27.1 14.3-42.9L179.8 320l118.1 0c12.2 0 22.1-9.9 22.1-22.1c0-6.3-2.7-12.3-7.4-16.5L38.6 37.9C34.3 34.1 28.9 32 23.2 32C10.4 32 0 42.4 0 55.2z"/%3E%3C/svg%3E') 0 0, auto;
    position: relative;
    display: block;
    order: 1;
    width: 100%;
    height: calc(100vh + 34px);
    height: calc(100svh + 34px);
    min-height: 620px;
    overflow: hidden;
  }
  .home-hero-media {
    position: absolute;
    inset: 0;
    display: block;
    width: 100%;
    height: 100%;
    background: #111113;
  }
  .home-hero-media video,
  .home-hero-media .home-video-poster,
  .home-hero-media .fallback-image {
    inset: -1px;
    width: calc(100% + 2px);
    height: calc(100% + 2px);
    object-fit: cover;
    cursor: var(--home-panel-point) !important;
  }
  .description-overlay { display: none; }
  .transition-overlay { display: none; }
  .home-hero-socials { display: none; }
  .home-current-panel { order: 2; }
  .home-ask-panel { order: 3; }
  .home-site-index { order: 4; }

  .home-current-panel,
  .home-ask-panel,
  .home-site-index {
    box-sizing: border-box;
    width: 100%;
    height: auto;
    min-height: 0;
    max-height: none;
    overflow: visible;
    border-bottom: 0;
    background: var(--home-page-surface);
  }

  .home-site-index {
    border-bottom: 0;
  }

  .home-hero-panel {
    --hero-gap: clamp(52px, 14vw, 76px);
    position: absolute;
    inset: -1px;
    z-index: 4;
    width: auto;
    height: auto;
    min-height: 0;
    background:
      linear-gradient(180deg, rgba(8, 8, 10, 0.54) 0%, rgba(8, 8, 10, 0.24) 46%, rgba(8, 8, 10, 0.62) 100%);
  }
  .home-hero-panel {
    justify-content: flex-start;
  }
  .home-hero-copy {
    justify-content: flex-start;
    background: transparent;
  }
  .home-hero-panel :deep(a),
  .home-hero-panel :deep(button),
  .home-hero-panel :deep([role="button"]) {
    cursor: var(--home-white-hand) !important;
  }
  .home-hero-copy {
    justify-content: flex-start;
    padding: var(--hero-gap) clamp(24px, 7vw, 38px) 28px;
  }
  .home-hero-copy h1 {
    color: #f5f5f7 !important;
    font-size: clamp(4.6rem, 23vw, 7.2rem) !important;
    line-height: 0.84;
  }
  .home-hero-copy h2 {
    max-width: 21ch;
    margin-top: 1.5rem;
    color: rgba(245, 245, 247, 0.88) !important;
    font-size: clamp(1.3rem, 6vw, 1.7rem) !important;
  }
  .home-hero-mobile-scene {
    position: absolute;
    right: clamp(24px, 7vw, 38px);
    bottom: var(--hero-gap);
    z-index: 5;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.8rem;
    pointer-events: auto;
  }
  .home-hero-mobile-note {
    display: flex;
    flex-direction: row;
    align-items: baseline;
    gap: 0.45rem;
    color: rgba(245, 245, 247, 0.68);
    font-family: "Quattrocento Sans", sans-serif;
    font-size: 0.9rem;
    font-weight: 400;
    letter-spacing: 0.22em;
    line-height: 1.3;
    text-align: right;
    text-decoration: none;
    text-transform: uppercase;
    transition:
      color 240ms cubic-bezier(0.22, 1, 0.36, 1),
      transform 240ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  .home-hero-mobile-note strong {
    color: #f5f5f7;
    display: inline-block;
    font-size: 1.08rem;
    font-weight: 600;
    letter-spacing: 0.26em;
    min-width: 48px;
  }
  :global(body.dark-mode .home-hero-mobile-note) { color: rgba(245, 245, 247, 0.68); }
  :global(body.dark-mode .home-hero-mobile-note strong) { color: #f5f5f7; }
  .home-hero-mobile-pwc {
    display: flex;
    flex-direction: row;
    align-items: baseline;
    gap: 0.45rem;
    color: rgba(245, 245, 247, 0.68);
    font-family: "Quattrocento Sans", sans-serif;
    font-size: 0.9rem;
    font-weight: 400;
    letter-spacing: 0.22em;
    line-height: 1.3;
    text-align: right;
    text-decoration: none;
    text-transform: uppercase;
    transition:
      color 240ms cubic-bezier(0.22, 1, 0.36, 1),
      transform 240ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  .home-hero-mobile-pwc-label {
    font-size: 0.9rem;
    font-weight: 400;
    letter-spacing: 0.22em;
    line-height: 1.3;
  }
  .home-hero-mobile-pwc-title {
    color: #f5f5f7;
    display: inline-block;
    font-size: 1.08rem;
    font-weight: 600;
    letter-spacing: 0.26em;
    line-height: 1.3;
    min-width: 48px;
    text-transform: none;
  }
  :global(body.dark-mode .home-hero-mobile-pwc) { color: rgba(245, 245, 247, 0.68); }
  :global(body.dark-mode .home-hero-mobile-pwc-title) { color: #f5f5f7; }
  @media (hover: hover) {
    .home-hero-mobile-note:hover {
      color: #f5f5f7;
      transform: translateY(-1px);
    }
    .home-hero-mobile-note:active { transform: translateY(0) scale(0.98); }
    .home-hero-mobile-pwc:hover { color: #f5f5f7; transform: translateY(-1px); }
    .home-hero-mobile-pwc:active { transform: translateY(0) scale(0.98); }
    :global(body.dark-mode .home-hero-mobile-note:hover) { color: #f5f5f7; }
    :global(body.dark-mode .home-hero-mobile-pwc:hover) { color: #f5f5f7; }
  }
  :global(body.dark-mode .home-hero-panel) {
    background:
      linear-gradient(180deg, rgba(8, 8, 10, 0.54) 0%, rgba(8, 8, 10, 0.24) 46%, rgba(8, 8, 10, 0.62) 100%);
  }
  :global(body.dark-mode .home-hero-copy) {
    background: transparent;
  }
  :global(body.dark-mode .home-hero-copy h1),
  :global(body.dark-mode .home-hero-copy h2) {
    color: #f5f5f7 !important;
  }
  :global(body.dark-mode .home-hero-panel a),
  :global(body.dark-mode .home-hero-panel button),
  :global(body.dark-mode .home-hero-panel [role="button"]) {
    cursor: var(--home-white-hand) !important;
  }
  .home-scene {
    display: none;
  }

  .home-current-panel,
  .home-ask-panel,
  .home-site-index { padding-inline: clamp(24px, 7vw, 38px); }
  .home-current-panel {
    position: relative;
    z-index: 5;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    margin: -50px 0 0;
    padding-top: clamp(92px, 24vw, 124px);
    padding-bottom: clamp(104px, 27vw, 140px);
    border-radius: 34px 34px 0 0;
  }
  .home-current-panel::before { display: none; }
  .home-current-panel > .home-section-eyebrow { display: none; }
  .home-current-panel h2 {
    display: block;
    margin: 0 0 clamp(2rem, 7vw, 2.75rem);
    color: inherit;
    font-family: "PT Serif", Georgia, serif;
    font-size: clamp(2.3rem, 11vw, 3.7rem);
    font-weight: 400;
    letter-spacing: -0.045em;
    line-height: 1;
  }
  .home-current-list { margin: 0; }
  .home-current-list a { display: grid; grid-template-columns: 2.5rem minmax(0, 1fr) auto; gap: 0.8rem; padding: clamp(1.15rem, 4.8vw, 1.45rem) 0; border-bottom: 1px solid var(--home-hairline); opacity: 1; }
  .home-current-list a:last-child { border-bottom: 0; }
  .home-current-arrow { opacity: 0.42; transform: translateX(0); }
  .home-current-list a:active .home-current-arrow { opacity: 1; }
  .home-current-number { display: block; overflow: visible; color: var(--home-muted); font-size: 0.72rem; opacity: 1; }
  .home-current-copy strong { color: #29292c; font-size: 1rem; font-weight: 750; }
  .home-current-copy small { font-size: 0.72rem; }
  .home-current-cv { display: none; }

  .home-ask-panel {
    --home-mobile-inline: clamp(24px, 7vw, 38px);
    padding-top: clamp(96px, 25vw, 132px);
    padding-bottom: clamp(110px, 29vw, 152px);
    justify-content: flex-start;
    overflow: visible;
    border: 0;
    background: var(--home-page-surface);
  }
  :global(body.dark-mode .home-ask-panel) {
    background: var(--home-page-surface);
  }
  .home-ask-stage { overflow: visible; }
  .home-ask-intro h2 { max-width: 9.5ch; margin: 1.45rem 0 1rem; font-size: clamp(2.35rem, 10.5vw, 3.45rem); line-height: 0.98; }
  .home-ask-title-wide { display: none; }
  .home-ask-title-mobile { display: block; }
  .home-ask-intro p { max-width: 25rem; font-size: 0.86rem; line-height: 1.55; }
  /* Mobile keeps its original choreography: the introduction leaves upward
     and the eyebrow rises from below. The desktop directions stay desktop. */
  .home-ask-panel > .home-section-eyebrow {
    transform: translateY(10px);
  }
  .home-ask-panel.is-engaged .home-ask-intro h2,
  .home-ask-panel.is-engaged .home-ask-intro p {
    opacity: 0;
    transform: translateY(-48px) scale(0.965);
  }
  .home-ask { flex: 0 1 auto; min-height: 0; margin-top: clamp(3.25rem, 14vw, 5.25rem); }
  /* The composer sits directly below "Ask the site", above the answer. */
  .home-ask-bar-slot { order: 1; }
  .home-ask-result { order: 2; }
  .home-ask-result {
    margin-top: clamp(1.4rem, 5vw, 2rem);
  }
  .home-ask-panel.is-engaged {
    height: auto;
    min-height: 0;
    max-height: none;
    overflow: visible;
    justify-content: start;
  }
  .home-ask-panel.is-engaged .home-ask-stage {
    min-height: 0;
    overflow: visible;
    justify-content: flex-start;
  }
  .home-ask-panel.is-engaged .home-ask {
    min-height: 0;
    overflow: visible;
    margin-top: 0.5rem;
  }
  .home-ask-result {
    flex: 0 0 auto;
    max-height: none;
    overflow: visible;
    overscroll-behavior-y: auto;
    -webkit-overflow-scrolling: touch;
    padding-right: 0;
  }
  .home-ask:has(.home-ask-result-busy) {
    flex: 0 0 auto;
  }
  .home-ask-result-busy {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
  }
  .home-ask-bar {
    min-height: 52px;
    transition: color 160ms ease;
  }
  .home-ask-bar-slot { min-height: 52px; }
  :global(body.dark-mode .home-current-copy strong) { color: #f5f5f7; }

  .home-site-index {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    padding-top: clamp(92px, 24vw, 124px);
    padding-bottom: clamp(84px, 22vw, 116px);
  }
  .home-site-index > .home-section-eyebrow,
  .home-site-index > nav {
    position: relative;
    top: 0;
  }
  .home-site-index nav { display: grid; margin-top: clamp(2rem, 7vw, 2.75rem); }
  .home-site-index a {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    padding: clamp(0.56rem, 2.3vw, 0.72rem) 0;
    border-bottom: 1px solid var(--home-hairline);
    color: inherit;
    font: 1.65rem/1.15 "PT Serif", Georgia, serif;
    text-decoration: none;
    transition:
      color 240ms cubic-bezier(0.22, 1, 0.36, 1),
      transform 240ms cubic-bezier(0.22, 1, 0.36, 1),
      border-color 240ms ease;
  }
  .home-site-index a small {
    color: var(--home-muted);
    font: 0.68rem/1 sans-serif;
    transition:
      color 240ms ease,
      transform 260ms cubic-bezier(0.22, 1, 0.36, 1),
      opacity 240ms ease;
  }
  @media (hover: hover) {
    .home-site-index a:hover,
    .home-site-index a:focus-visible {
      color: #171719;
      transform: translateX(4px);
      border-color: rgba(var(--site-accent-rgb), 0.42);
    }
    .home-site-index a:hover small,
    .home-site-index a:focus-visible small {
      color: var(--site-accent-strong);
      opacity: 1;
      transform: translateY(-1px);
    }
    .home-site-index a:active { transform: translateX(4px) scale(0.985); }
    :global(body.dark-mode .home-site-index a:hover),
    :global(body.dark-mode .home-site-index a:focus-visible) { color: #f5f5f7; }
  }
  :global(body.dark-mode .home-hero-copy) { background: transparent; }
}

@media (max-width: 380px), (max-height: 680px) and (max-width: 760px) {
  .home-hero-copy h1 { font-size: clamp(4rem, 20vw, 5.4rem) !important; }
  .home-hero-copy h2 { margin-top: 1.05rem; font-size: 1.3rem !important; }
  .home-hero-section { height: calc(100vh + 34px); height: calc(100svh + 34px); min-height: 590px; }
  .home-current-panel { padding-top: 84px; padding-bottom: 96px; }
  .home-ask-panel { padding-top: 88px; padding-bottom: 104px; }
  .home-site-index { padding-top: 84px; padding-bottom: 78px; }
  .home-ask { min-height: 0; margin-top: 2.5rem; }
}

@media (prefers-reduced-motion: reduce) {
  .home-ask-bar::before,
  .home-ask-bar::after {
    transition: background-color 120ms linear;
    transform: none;
  }
  .home-ask-intro,
  .home-ask-panel.is-engaged .home-ask-intro,
  .home-ask-panel > .home-section-eyebrow,
  .home-ask-panel.is-engaged > .home-section-eyebrow,
  .home-current-list a,
  .home-ask-submit,
  .home-prompt-fade-enter-active,
  .home-prompt-fade-leave-active { transition: none; }
  .home-hero-media video { filter: none; }
  .description-content :deep(.home-video-sound-state),
  .description-content :deep(.home-video-sound-wave) { animation: none !important; }
}
</style>
